import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DateField from '../../components/Shared/DateField';
import ChipSelect from '../../components/Shared/ChipSelect';
import MarcaOSIMTracker, { emptyRecord as emptyOSIM } from '../../components/Tracker/MarcaOSIM';
import MarcaEUIPOTracker, { emptyRecord as emptyEUIPO } from '../../components/Tracker/MarcaEUIPO';
import MarcaWIPOTracker, { emptyRecord as emptyWIPO } from '../../components/Tracker/MarcaWIPO';
import MarcaInternationalaTracker, { emptyRecord as emptyMarcaInt } from '../../components/Tracker/MarcaInternationala';
import IDepotTracker, { emptyRecord as emptyIDepot } from '../../components/Tracker/IDepot';
import DesignMITracker, { emptyRecord as emptyDMI } from '../../components/Tracker/DesignMI';
import BrevetTracker, { emptyRecord as emptyBrevet } from '../../components/Tracker/Brevet';
import OpozitieOSIMTracker, { emptyRecord as emptyOpozitieOSIM } from '../../components/Tracker/OpozitieOSIM';
import OpozitieEUIPOTracker, { emptyRecord as emptyOpozitieEUIPO } from '../../components/Tracker/OpozitieEUIPO';
import ContestatieOSIMTracker, { emptyRecord as emptyContestatieOSIM } from '../../components/Tracker/ContestatieOSIM';
import CaiAtacEUIPOTracker, { emptyRecord as emptyCaiAtacEUIPO } from '../../components/Tracker/CaiAtacEUIPO';
import DosarInstantaTracker, { emptyRecord as emptyDosarInstanta } from '../../components/Tracker/DosarInstanta';
import NotificareAvizTracker, { emptyRecord as emptyNotificareAviz } from '../../components/Tracker/NotificareAviz';
import {
  createBrand,
  createClient,
  deleteBrand,
  deleteClient,
  listClienti,
  updateBrand,
  updateClient,
} from '../../services/clienti';
import { createProcedura, deleteProcedura, listProceduri, updateProcedura } from '../../services/proceduri';
import { uploadProceduraFile } from '../../services/proceduraFiles';
import { deleteDocument, getMediaUrl, listDocuments, uploadDocument } from '../../services/documents';
import { generateContract, generateDocument, listGeneratedDocs, redownloadDocument } from '../../services/contracts';
import * as smartbillSvc from '../../services/smartbill';
import { sendFacturiEmail } from '../../services/smartbill';
import { CAT_AVIZE, CAT_IP, CAT_LIT, NICECLASS_OPTIONS, OFFICE_TERRITORY_MAP, OFFICES_OPTIONS, TERRITORIES_OPTIONS } from '../../utils/procDefs';
import { fmtDateRO } from '../../utils/dateFmt';
// getProcWorstSev stays client-side deliberately: it's used below on live,
// unsaved React form state (ProceduraItem's localData, edited before saving),
// which an API field can only ever reflect after a save — see Phase 0, 0.4 in
// ai-agent-implementation-plan.md. SEV is just the static color palette.
import { getProcWorstSev, SEV } from '../../utils/alerts';
import InvoiceEditModal from '../../components/Shared/InvoiceEditModal';
import FacturaModal, { COMPANIE_OPTIONS, MONEDA_OPTIONS, LIMBA_OPTIONS, TIP_PROC_LABEL, emptyProdus, emptyMeta } from '../../components/Shared/FacturaModal';
import { FilterPill } from '../../components/Shared/FilterPill';
import { useAuth } from '../../hooks/useAuth';
import { listCheltuieli, createCheltuiala, patchCheltuiala, deleteCheltuiala, uploadCheltuialaFile, deleteCheltuialaFile } from '../../services/cheltuieli';
import { getIstoricClient } from '../../services/istoric';
import { getAlerts } from '../../services/alerts';
import apiClient from '../../services/apiClient';
import './ClientiPage.css';


/* Types with a real tracker component wired into the Wizard/ProceduraItem. */
const EMPTY_RECORD_MAP = {
  marca_osim:           emptyOSIM,
  marca_euipo:          emptyEUIPO,
  marca_wipo:           emptyWIPO,
  marca_internationala: emptyMarcaInt,
  idepot:           emptyIDepot,
  dmi:              emptyDMI,
  brevet:           emptyBrevet,
  opozitie_osim:    emptyOpozitieOSIM,
  opozitie_euipo:   emptyOpozitieEUIPO,
  contestatie_osim: emptyContestatieOSIM,
  cai_atac_euipo:   emptyCaiAtacEUIPO,
  dosar_instanta:   emptyDosarInstanta,
  notificare_aviz:  emptyNotificareAviz,
};

const TRACKER_MAP = {
  marca_osim:           MarcaOSIMTracker,
  marca_euipo:          MarcaEUIPOTracker,
  marca_wipo:           MarcaWIPOTracker,
  marca_internationala: MarcaInternationalaTracker,
  idepot:           IDepotTracker,
  dmi:              DesignMITracker,
  brevet:           BrevetTracker,
  opozitie_osim:    OpozitieOSIMTracker,
  opozitie_euipo:   OpozitieEUIPOTracker,
  contestatie_osim: ContestatieOSIMTracker,
  cai_atac_euipo:   CaiAtacEUIPOTracker,
  dosar_instanta:   DosarInstantaTracker,
  notificare_aviz:  NotificareAvizTracker,
};

/* ── Toast ──────────────────────────────────────────────── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

function ToastWrap({ toasts }) {
  return (
    <div className="toast-wrap">
      {toasts.map(t => <div key={t.id} className="toast">{t.msg}</div>)}
    </div>
  );
}

/* ── Wizard ──────────────────────────────────────────────── */
function Wizard({ clientId, onClose, onSaved, showToast }) {
  const [step, setStep] = useState(1);
  const [cat, setCat] = useState(null);
  const [procId, setProcId] = useState(null);
  const [procData, setProcData] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const steps = ['Tip procedura', 'Procedura', 'Formular'];

  const selectProc = (id) => {
    const empty = EMPTY_RECORD_MAP[id];
    if (empty) {
      setProcId(id);
      setProcData(empty());
      setStep(3);
    } else {
      const all = [...CAT_IP, ...CAT_AVIZE, ...CAT_LIT];
      const proc = all.find(p => p.id === id);
      showToast(`${proc?.name || id} — in curand`);
    }
  };

  const goBack = () => {
    if (step === 3) { setStep(2); setProcId(null); setProcData(null); setPendingFiles([]); }
    else if (step === 2) { setStep(1); setCat(null); }
    else onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await createProcedura(clientId, { tip_proc: procId, data: procData || {} });
      for (const pf of pendingFiles) {
        try {
          await uploadProceduraFile(result.procedura.id, pf.denumire, pf.file);
        } catch {
          showToast(`Eroare la incarcarea fisierului "${pf.file.name}"`);
        }
      }
      onSaved(result.procedura, result.brand || null);
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Eroare la salvare procedura');
    } finally {
      setSaving(false);
    }
  };

  const wizTitle = (() => {
    if (step !== 3 || !procId) return 'Procedura Noua';
    const catLabel = CAT_IP.some(p => p.id === procId)    ? 'Inregistrare IP'
                   : CAT_AVIZE.some(p => p.id === procId) ? 'Avize / Opozitii'
                   : CAT_LIT.some(p => p.id === procId)   ? 'Litigiu'
                   : '';
    const tipLabel = TIP_PROC_LABEL[procId] || procId;
    return catLabel ? `${catLabel} — ${tipLabel}` : tipLabel;
  })();

  return (
    <div className="wizard-box">
      <div className="wiz-top">
        <div className="wiz-title">{wizTitle}</div>
        <button className="btn btn-ghost btn-xs" type="button" onClick={onClose}>✕</button>
      </div>
      <div className="wiz-steps">
        {steps.map((lbl, i) => {
          const n = i + 1;
          const cls = n < step ? 'ws-done' : n === step ? 'ws-active' : '';
          return (
            <React.Fragment key={lbl}>
              {i > 0 && <div className={`ws-line${n <= step ? ' ws-line-done' : ''}`} />}
              <div className={`wiz-step ${cls}`}>
                <div className="ws-num">{n < step ? '✓' : n}</div>
                {lbl}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {step === 1 && (
        <div>
          <p className="wiz-hint">Selectati tipul de procedura:</p>
          <div className="choice-grid">
            <div className="choice-card" onClick={() => { setCat('ip'); setStep(2); }}>
              <div className="cc-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
              </div>
              <div className="cc-title">Inregistrare IP</div>
              <div className="cc-desc">Marca, brevet, design industrial, I-Depot</div>
            </div>
            <div className="choice-card" onClick={() => { setCat('avize'); setStep(2); }}>
              <div className="cc-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
              <div className="cc-title">Avize Refuz / Opozitii</div>
              <div className="cc-desc">Opozitii OSIM/EUIPO, contestatii, cai de atac, avize</div>
            </div>
            <div className="choice-card" onClick={() => { setCat('litigiu'); setStep(2); }}>
              <div className="cc-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <div className="cc-title">Litigiu</div>
              <div className="cc-desc">Dosare instanta, notificari marketplace</div>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <p className="wiz-hint">Selectati procedura specifica:</p>
          <div className="sub-grid">
            {(cat === 'ip' ? CAT_IP : cat === 'avize' ? CAT_AVIZE : CAT_LIT).map(p => (
              <div key={p.id} className="sub-card" onClick={() => selectProc(p.id)}>
                <div className="sc-icon">{p.icon}</div>
                <div className="sc-title">{p.name}</div>
                <div className="sc-sub">{p.sub}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 3 && TRACKER_MAP[procId] && (() => {
        const Tracker = TRACKER_MAP[procId];
        return <Tracker data={procData} onChange={setProcData} showToast={showToast} pendingFiles={pendingFiles} onPendingFilesChange={setPendingFiles} />;
      })()}

      {step > 1 && (
        <div className="wiz-footer">
          <button className="btn btn-ghost btn-sm" type="button" onClick={goBack}>← Inapoi</button>
          {step === 3 && (
            <button className="btn btn-primary btn-sm" type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Se salveaza...' : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Salveaza Procedura</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── AddBrandForm ────────────────────────────────────────── */
// Auto-build "Cuvinte cheie de cautare" from the mark name, per the OSIM/BOPI
// similar-terms search rules:
//   1. Termenul complet ("STALINSKAYA SPRING TIME")
//   2. Termenii separati, cate un cuvant ("STALINSKAYA", "SPRING", "TIME")
//   3. Termenii grupati — toate combinatiile de 2 cuvinte, nu doar cele
//      adiacente ("STALINSKAYA SPRING", "STALINSKAYA TIME", "SPRING TIME")
//   4-11. Substitutii de litere (S->Z/X, K->C, T->TH/TT, B->V, E->3, F->PH,
//      I->Y), aplicate DOAR pe termenii separati (2) — nu si pe termenul
//      complet sau pe grupari, ca sa nu explodeze lista (ar fi 60-150+ intrari
//      pe un nume din 3 cuvinte in loc de ~20-30).
//   12. Si substitutiile inverse (Z/X->S, C->K, TH/TT->T, V->B, 3->E, PH->F,
//      Y->I) — pentru cazul in care numele real contine deja litera "de
//      schimbat" (ex. un nume cu "PH" trebuie sa genereze si varianta cu "F").
//   13. Ferestre de 5 litere pe fiecare termen separat (2) — nu primele 5
//      litere, ci toate combinatiile de 5 litere consecutive, ca la vechea
//      logica de 4 litere ("STALINSKAYA" -> "STALI, TALIN, ALINS, LINSK,
//      INSKA, NSKAY, SKAYA"). Direct din numele original, fara substitutii.
//      Doar pentru cuvinte cu peste 5 litere.
const KEYWORD_SUBSTITUTIONS = [
  ['S', 'Z'], ['S', 'X'],
  ['K', 'C'],
  ['T', 'TH'], ['T', 'TT'],
  ['B', 'V'],
  ['E', '3'],
  ['F', 'PH'],
  ['I', 'Y'],
];

function buildKeywordsFromName(rawName) {
  const full = (rawName || '').trim().replace(/\s+/g, ' ').toUpperCase();
  if (!full) return '';
  const words = full.split(' ').filter(Boolean);

  const terms = new Set();
  terms.add(full);
  words.forEach(w => terms.add(w));

  for (let i = 0; i < words.length; i++) {
    for (let j = i + 1; j < words.length; j++) {
      terms.add(`${words[i]} ${words[j]}`);
    }
  }

  words.forEach(w => {
    KEYWORD_SUBSTITUTIONS.forEach(([from, to]) => {
      if (w.includes(from)) terms.add(w.split(from).join(to));
      if (w.includes(to)) terms.add(w.split(to).join(from));
    });
  });

  words.forEach(w => {
    if (w.length > 5) {
      for (let i = 0; i + 5 <= w.length; i++) {
        terms.add(w.slice(i, i + 5));
      }
    }
  });

  return [...terms].join(', ');
}

function AddBrandForm({ clientId, onCancel, onSaved, showToast, editBrand = null }) {
  const isEdit = !!editBrand;
  const [form, setForm] = useState(editBrand ? {
    name: editBrand.name || '', tip_proprietate: editBrand.tip_proprietate || '',
    nr_inregistrare: editBrand.nr_inregistrare || '', observatii: editBrand.observatii || '',
    data_inregistrare: editBrand.data_inregistrare || '', data_expirare: editBrand.data_expirare || '',
    oficiu_inreg: editBrand.oficiu_inreg || '',
    monitoring_activ: editBrand.monitoring_activ || false,
    keywords: editBrand.keywords || '', data_start_monitoring: editBrand.data_start_monitoring || '',
    data_stop_monitoring: editBrand.data_stop_monitoring || '', criterii_monitoring: editBrand.criterii_monitoring || '',
    clasa_nice_text: editBrand.clasa_nice_text || '', offices: editBrand.offices || [],
    teritorii: editBrand.teritorii || [], nice_classes: editBrand.nice_classes || [],
    trimite_email_client: editBrand.trimite_email_client || false, email_rapoarte: editBrand.email_rapoarte || '',
    reinnoire_anuntat_client:  editBrand.reinnoire_anuntat_client || false,
    reinnoire_depusa:          editBrand.reinnoire_depusa || false,
    reinnoire_ciclu_expirare:  editBrand.reinnoire_ciclu_expirare || null,
  } : {
    name: '', tip_proprietate: '', nr_inregistrare: '', observatii: '',
    data_inregistrare: '', data_expirare: '', oficiu_inreg: '',
    monitoring_activ: false, keywords: '', data_start_monitoring: '', data_stop_monitoring: '',
    criterii_monitoring: '', clasa_nice_text: '', offices: [], teritorii: [], nice_classes: [],
    trimite_email_client: false, email_rapoarte: '',
    reinnoire_anuntat_client: false, reinnoire_depusa: false, reinnoire_ciclu_expirare: null,
  });
  const [saving, setSaving] = useState(false);

  // Once the user manually edits the keywords, stop auto-regenerating them from
  // the name. Pre-set as "touched" when editing a brand that already has some.
  const keywordsTouchedRef = useRef(isEdit && !!(editBrand && editBrand.keywords));

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleNameChange = (v) => {
    setForm(f => ({
      ...f,
      name: v,
      keywords: keywordsTouchedRef.current ? f.keywords : buildKeywordsFromName(v),
    }));
  };

  const handleMonitoringToggle = (on) => {
    setForm(f => ({
      ...f,
      monitoring_activ: on,
      keywords: (on && !keywordsTouchedRef.current && !f.keywords)
        ? buildKeywordsFromName(f.name)
        : f.keywords,
    }));
  };

  const handleKeywordsChange = (v) => {
    keywordsTouchedRef.current = true;
    set('keywords', v);
  };

  const handleOfficesChange = (nextOffices) => {
    const added   = nextOffices.filter(o => !form.offices.includes(o));
    const removed = form.offices.filter(o => !nextOffices.includes(o));

    let newTeritorii = [...form.teritorii];

    added.forEach(office => {
      (OFFICE_TERRITORY_MAP[office] || []).forEach(t => {
        if (!newTeritorii.includes(t)) newTeritorii.push(t);
      });
    });

    removed.forEach(office => {
      (OFFICE_TERRITORY_MAP[office] || []).forEach(territory => {
        const keptByOther = nextOffices.some(o => (OFFICE_TERRITORY_MAP[o] || []).includes(territory));
        if (!keptByOther) newTeritorii = newTeritorii.filter(t => t !== territory);
      });
    });

    setForm(f => ({ ...f, offices: nextOffices, teritorii: newTeritorii }));
  };

  const handleTerritoriesChange = (nextTeritorii) => {
    const removed = form.teritorii.filter(t => !nextTeritorii.includes(t));

    let newOffices = [...form.offices];

    removed.forEach(territory => {
      newOffices = newOffices.filter(office => {
        const linked = OFFICE_TERRITORY_MAP[office] || [];
        return !linked.includes(territory);
      });
    });

    Object.entries(OFFICE_TERRITORY_MAP).forEach(([office, linked]) => {
      if (linked.length > 0 && linked.every(t => nextTeritorii.includes(t)) && !newOffices.includes(office)) {
        newOffices.push(office);
      }
    });

    setForm(f => ({ ...f, teritorii: nextTeritorii, offices: newOffices }));
  };

  const save = async () => {
    if (!form.name.trim()) { showToast('Introduceti denumirea marcii'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.data_start_monitoring) delete payload.data_start_monitoring;
      if (!payload.data_stop_monitoring) delete payload.data_stop_monitoring;
      if (isEdit) {
        const data = await updateBrand(editBrand.id, payload);
        showToast('✓ Marca actualizata!');
        onSaved(data.brand, data.procedura || null);
      } else {
        const data = await createBrand(clientId, payload);
        showToast('✓ Marca adaugata cu succes!');
        onSaved(data.brand, data.procedura || null);
      }
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="add-brand-form">
      <div className="abf-title">{isEdit ? `Editeaza: ${editBrand.name}` : 'Marca Noua'}</div>

      <div className="f-row mb10">
        <div className="field"><label className="req">Denumire marca</label><input type="text" value={form.name} onChange={e => handleNameChange(e.target.value)} placeholder="ex: Aurora Style" /></div>
        <div className="field">
          <label className="req">Tip proprietate</label>
          <FilterPill searchable value={form.tip_proprietate} onChange={e => set('tip_proprietate', e.target.value)}>
            <option value="">-- Selecteaza --</option>
            {['Marca verbala', 'Marca figurativa', 'Marca combinata', 'Marca stilizata', 'Marca tridimensionala', 'Marca sonora', 'Design industrial', 'Brevet de inventie', 'Model de utilitate', 'Alt tip'].map(o => <option key={o} value={o}>{o}</option>)}
          </FilterPill>
        </div>
      </div>
      <div className="field mb10">
        <label>Nr. inregistrare (daca exista)</label>
        <input type="text" value={form.nr_inregistrare} onChange={e => set('nr_inregistrare', e.target.value)} placeholder="ex: M 2024/01234" />
      </div>
      <div className="f-row f-row-3 mb10">
        <div className="field">
          <label>Oficiu inregistrare</label>
          <FilterPill searchable value={form.oficiu_inreg} onChange={e => set('oficiu_inreg', e.target.value)}>
            <option value="">— Selecteaza —</option>
            {['OSIM', 'EUIPO', 'WIPO', 'USPTO', 'IPO-UK', 'CIPO', 'BOIP', 'EPO', 'ILPO', 'AGEPI', 'INDECOPI', 'Altele'].map(o => <option key={o} value={o}>{o}</option>)}
          </FilterPill>
        </div>
        <div className="field"><label>Data inregistrare marca</label><DateField value={form.data_inregistrare} onChange={v => set('data_inregistrare', v)} /></div>
        <div className="field"><label>Data expirare marca</label><DateField value={form.data_expirare} onChange={v => set('data_expirare', v)} /></div>
      </div>
      <div className="field mb10"><label>Clase Nisa</label><ChipSelect options={NICECLASS_OPTIONS} value={form.nice_classes} onChange={v => set('nice_classes', v)} placeholder="Selecteaza clase Nisa..." /></div>
      <div className="field mb14"><label>Observatii interne</label><input type="text" value={form.observatii} onChange={e => set('observatii', e.target.value)} placeholder="Note scurte despre aceasta marca..." /></div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', marginBottom: 8 }}>Reinnoire</div>
        <label className="check-label" style={{ marginBottom: 6 }}>
          <input type="checkbox" checked={form.reinnoire_anuntat_client} onChange={e => set('reinnoire_anuntat_client', e.target.checked)} />
          Anuntat clientul
        </label>
        <label className="check-label">
          <input type="checkbox" checked={form.reinnoire_depusa} onChange={e => {
            const checked = e.target.checked;
            setForm(f => ({ ...f, reinnoire_depusa: checked, reinnoire_ciclu_expirare: checked ? null : null }));
          }} />
          Reinnoirea depusa
        </label>
        {form.reinnoire_depusa && (
          <div className="field" style={{ marginTop: 8 }}>
            <label>Data noua de expirare</label>
            <DateField value={form.data_expirare} onChange={v => setForm(f => ({
              ...f,
              data_expirare: v,
              reinnoire_ciclu_expirare: v || null,
            }))} />
          </div>
        )}
      </div>

      <label className="switch mb4">
        <input type="checkbox" checked={form.monitoring_activ} onChange={e => handleMonitoringToggle(e.target.checked)} />
        <span className="slider" />
        <span className="switch-label">Monitorizare activa in Surf?</span>
      </label>
      <div className="mon-hint">Activeaza pentru a configura campania de monitorizare legata de aceasta marca</div>

      {form.monitoring_activ && (
        <div className="monitoring-section mon-visible">
          <div className="section-label" style={{ marginBottom: 10 }}>Configurare monitorizare Surf</div>

          <div className="field mb10">
            <label className="req">Cuvinte cheie de cautare</label>
            <textarea value={form.keywords} onChange={e => handleKeywordsChange(e.target.value)} rows="2" placeholder="ex: aurora style, aurora prod" style={{ minHeight: 60 }} />
            <div className="f-hint">Separa cu virgule. Se completeaza automat din denumire; poti edita liber.</div>
          </div>

          <div className="f-row mb10">
            <div className="field"><label>Data start monitorizare</label><DateField value={form.data_start_monitoring} onChange={v => set('data_start_monitoring', v)} /></div>
            <div className="field"><label>Data stop monitorizare</label><DateField value={form.data_stop_monitoring} onChange={v => set('data_stop_monitoring', v)} /></div>
          </div>

          <div className="f-row mb10">
            <div className="field"><label>Criterii / observatii monitorizare</label><textarea value={form.criterii_monitoring} onChange={e => set('criterii_monitoring', e.target.value)} rows="2" placeholder="Reguli sau observatii specifice..." style={{ minHeight: 60 }} /></div>
            <div className="field"><label>Clasa Nice (text liber)</label><input type="text" value={form.clasa_nice_text} onChange={e => set('clasa_nice_text', e.target.value)} placeholder="ex: 9, 35, 42" /><div className="f-hint">Sau foloseste selectorul de mai jos</div></div>
          </div>

          <div className="field mb10"><label>Offices</label><ChipSelect options={OFFICES_OPTIONS} value={form.offices} onChange={handleOfficesChange} placeholder="Selecteaza oficii..." /></div>
          <div className="field mb10"><label>Teritorii</label><ChipSelect options={TERRITORIES_OPTIONS} value={form.teritorii} onChange={handleTerritoriesChange} placeholder="Selecteaza teritorii..." /></div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 2 }}>
            <label className="switch">
              <input type="checkbox" checked={form.trimite_email_client} onChange={e => set('trimite_email_client', e.target.checked)} />
              <span className="slider" />
              <span className="switch-label">Trimite email la client</span>
            </label>
            {form.trimite_email_client && (
              <div className="field"><label>Email client (pentru rapoarte)</label><input type="email" value={form.email_rapoarte} onChange={e => set('email_rapoarte', e.target.value)} placeholder="client@exemplu.ro" /></div>
            )}
          </div>
        </div>
      )}

      <div className="brand-actions">
        <button className="btn btn-primary btn-sm" type="button" onClick={save} disabled={saving}>{saving ? 'Se salveaza...' : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> {isEdit ? 'Salveaza modificarile' : 'Salveaza Marca'}</>}</button>
        <button className="btn btn-ghost btn-sm" type="button" onClick={onCancel}>Anuleaza</button>
      </div>
    </div>
  );
}

/* ── ProceduraItem ───────────────────────────────────────── */
// Identifying line shown on a procedure card (name · number · date). Marci keep
// their own denumire/numar/dataDepunere; the litigation-style trackers store
// these under different keys, which is why they used to show only the type label.
function procCardMeta(tip, d = {}) {
  switch (tip) {
    case 'dosar_instanta': {
      const faza = d.faze?.[d.fazaCurenta] || d.faze?.fond || {};
      return { name: d.denumire || d.obiect || '', nr: faza.numarDosar || d.faze?.fond?.numarDosar || '', date: d.dataDeschidere || '', dateLabel: 'deschis', adv: d.adversar || '' };
    }
    case 'opozitie_osim':
      return { name: d.marca || '', nr: d.numarOpozitie || d.numarDepozit || '', date: d.dataOpozitie || '', dateLabel: 'din', adv: d.oponent || '' };
    case 'opozitie_euipo':
      return { name: d.marca || '', nr: d.numarOpozitie || d.numarCerere || '', date: d.dataOpozitie || '', dateLabel: 'din', adv: d.oponent || '' };
    case 'contestatie_osim':
      return { name: d.denumire || '', nr: d.numarContestatie || d.numarDosar || '', date: d.dataDepunere || '', dateLabel: 'depusa' };
    case 'cai_atac_euipo':
      return { name: d.denumire || '', nr: d.numarRecurs || d.numarDosar || '', date: d.dataNotificare || '', dateLabel: 'notificat', adv: d.adversar || '' };
    case 'notificare_aviz':
      return { name: d.denumire || '', nr: d.numarCerere || '', date: d.dataComunicareNotif || '', dateLabel: 'comunicat' };
    default:
      return { name: d.denumire || '', nr: d.numar || '', date: d.dataDepunere || '', dateLabel: 'depus' };
  }
}

function ProceduraItem({ proc, onDelete, onUpdated, showToast }) {
  const [open, setOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [localData, setLocalData] = useState(proc.data || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setLocalData(proc.data || {}); }, [proc]);

  const label = TIP_PROC_LABEL[proc.tip_proc] || proc.tip_proc;
  const { name: denumire, nr: numar, date: dataDepunere, dateLabel, adv } = procCardMeta(proc.tip_proc, localData || {});
  const Tracker = TRACKER_MAP[proc.tip_proc] || null;
  const worstSev = getProcWorstSev(localData, proc.tip_proc);
  const dotColor = worstSev ? SEV[worstSev].dot : '#cbd5e1';

  const handleDelete = async () => {
    try {
      await deleteProcedura(proc.id);
      onDelete(proc.id);
      showToast('Procedura stearsa');
    } catch {
      showToast('Eroare la stergere');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateProcedura(proc.id, { data: localData });
      if (onUpdated) onUpdated({ ...proc, data: localData, ...result.procedura }, result.brand || null);
      showToast('✓ Procedura salvata');
    } catch {
      showToast('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`brand-item${open ? ' bi-open' : ''}`}>
      <div className="bi-header" onClick={() => setOpen(o => !o)}>
        <div className="bi-dot" style={{ background: dotColor }} />
        <div className="bi-info">
          <div className="bi-name">{label}</div>
          {(denumire || numar || dataDepunere || adv) && (
            <div className="bi-meta">
              {denumire && <span>{denumire}</span>}
              {numar && <span >{numar}</span>}
              {adv && <span>vs {adv}</span>}
              {dataDepunere && <span>{dateLabel} {fmtDateRO(dataDepunere)}</span>}
            </div>
          )}
        </div>
        <div className="proc-actions" style={{ marginRight: 8 }} onClick={e => e.stopPropagation()}>
          {confirmDel ? (
            <>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>Sigur stergi?</span>
              <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444' }} onClick={handleDelete}>Da</button>
              <button className="btn btn-ghost btn-xs" onClick={() => setConfirmDel(false)}>Nu</button>
            </>
          ) : (
            <button className="btn btn-ghost btn-xs" onClick={() => setConfirmDel(true)}>✕</button>
          )}
        </div>
        <div className="bi-right">
          <span className="bi-arrow">›</span>
        </div>
      </div>

      {open && (
        <div className="bi-body">
          {Tracker ? (
            <>
              <Tracker
                data={localData}
                onChange={setLocalData}
                onPersisted={(data) => { setLocalData(data); if (onUpdated) onUpdated({ ...proc, data }); }}
                procId={proc.id}
                clientId={proc.client_id}
                showToast={showToast}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Se salveaza...' : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Salveaza</>}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Inchide</button>
              </div>
            </>
          ) : (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '6px 0' }}>
              Tracker pentru <strong>{label}</strong> — in curand.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Dot color for a brand's renewal urgency. `sev` comes from the backend's
// /api/alerts/ (main.rules.alerts.build_alerts, ported 1:1 from the old
// alerts.js and verified against real data — see Phase 0 in
// ai-agent-implementation-plan.md). No matching alert (satisfied cycle, or
// outside the -180..365 day window) means no dot — same as before.
//
// NOTE: this replaces an inline reimplementation that had a real pre-existing
// bug — it collapsed the -150..-90 day "negru" (most severe) range into
// "rosu" by mistake. Fixed as part of this migration (user-confirmed): a
// brand overdue 91-149 days now correctly shows black instead of red.
function brandAlertDot(sev) {
  return sev ? SEV[sev].dot : '#cbd5e1';
}

/* ── BrandItem ───────────────────────────────────────────── */
function BrandItem({ brand: initialBrand, alertSev, showToast, onBrandUpdated, onBrandDeleted }) {
  const [brand, setBrand] = useState(initialBrand);
  const [open, setOpen] = useState(false);
  useEffect(() => { setBrand(initialBrand); }, [initialBrand]);
  const [showEditBrand, setShowEditBrand] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleBrandSaved = (updated, procedura) => {
    setBrand(updated);
    setShowEditBrand(false);
    if (onBrandUpdated) onBrandUpdated(updated, procedura);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteBrand(brand.id);
      if (onBrandDeleted) onBrandDeleted(brand.id);
    } catch {
      showToast('Eroare la stergere marca');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className={`brand-item${open ? ' bi-open' : ''}`}>
      <div className="bi-header" onClick={() => setOpen(o => !o)}>
        <div className="bi-dot" style={{ background: brandAlertDot(alertSev) }} />
        <div className="bi-info">
          <div className="bi-name">{brand.name}</div>
          <div className="bi-meta">{brand.tip_proprietate || 'Marca'}{brand.monitoring_activ ? ' · Monitorizare activa' : ''}</div>
        </div>
        <div className="proc-actions" style={{ marginRight: 8 }} onClick={e => e.stopPropagation()}>
          {confirmDelete ? (
            <>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>Sigur stergi?</span>
              <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444' }} disabled={deleting} onClick={handleDelete}>{deleting ? '...' : 'Da'}</button>
              <button className="btn btn-ghost btn-xs" onClick={() => setConfirmDelete(false)}>Nu</button>
            </>
          ) : (
            <button className="btn btn-ghost btn-xs" onClick={() => setConfirmDelete(true)}>✕</button>
          )}
        </div>
        <div className="bi-right">
          <span className="bi-arrow">›</span>
        </div>
      </div>
      {open && (
        <div className="bi-body">
          <div className="sec-head" style={{ marginBottom: 8 }}>
            <div className="sec-title" style={{ fontSize: 12.5 }}>Monitorizare</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="btn btn-ghost btn-xs" type="button" onClick={() => setShowEditBrand(e => !e)}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Editeaza
              </button>
            </div>
          </div>

          {showEditBrand && (
            <AddBrandForm
              clientId={null}
              onCancel={() => setShowEditBrand(false)}
              onSaved={handleBrandSaved}
              showToast={showToast}
              editBrand={brand}
            />
          )}

          {!showEditBrand && (
            <div style={{ padding: '6px 0', fontSize: 13 }}>
              <div style={{ color: 'var(--muted)' }}>
                {brand.keywords ? <><strong>Keywords:</strong> {brand.keywords}</> : 'Nicio configuratie monitorizare.'}
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', display: 'inline-block', background: brand.reinnoire_anuntat_client ? '#16a34a' : '#d1d5db' }} />
                  <span style={{ color: brand.reinnoire_anuntat_client ? '#166534' : 'var(--muted)' }}>Anuntat clientul</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', display: 'inline-block', background: brand.reinnoire_depusa ? '#16a34a' : '#d1d5db' }} />
                  <span style={{ color: brand.reinnoire_depusa ? '#166534' : 'var(--muted)' }}>Reinnoirea depusa</span>
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


const TIP_DOC_LABEL = {
  copie_buletin:  'Copie Buletin',
  copie_cui:      'Copie CUI',
  copie_contract: 'Copie Contract',
  copie_procura:  'Copie Procura',
  declaratie_sme: 'Declaratie SME',
  altele:         'Altele',
};

const SVC_LABEL = { taxe: 'Taxe', onorariu: 'Onorariu', mixt: 'Taxe + Onorariu' };

const MONEDE = ['RON', 'EUR', 'USD', 'GBP', 'CHF', 'CAD', 'YEN'];

// Județele României — doar sugestii de autocomplete; câmpul acceptă orice text
// (regiune/stat pentru clienții din străinătate). Valoarea se trimite ca atare la SmartBill.
const JUDETE = [
  'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani', 'Brașov',
  'Brăila', 'București', 'Buzău', 'Caraș-Severin', 'Călărași', 'Cluj', 'Constanța',
  'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 'Gorj', 'Harghita', 'Hunedoara',
  'Ialomița', 'Iași', 'Ilfov', 'Maramureș', 'Mehedinți', 'Mureș', 'Neamț', 'Olt',
  'Prahova', 'Satu Mare', 'Sălaj', 'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea',
  'Vaslui', 'Vâlcea', 'Vrancea',
];

/* ── ContractSemnatCheckbox ──────────────────────────────── */
function ContractSemnatCheckbox({ contract, clientId, showToast }) {
  const [semnat, setSemnat] = useState(contract.semnat);
  const [saving, setSaving] = useState(false);

  const handleChange = async (e) => {
    const val = e.target.checked;
    setSaving(true);
    try {
      const res = await apiClient.patch(`/clienti/${clientId}/contract/${contract.company}/semnat/`, { semnat: val });
      setSemnat(res.data.semnat);
    } catch {
      showToast('Eroare la actualizarea contractului');
    } finally {
      setSaving(false);
    }
  };

  return (
    <label className="check-label" style={{ marginTop: 8 }}>
      <input type="checkbox" checked={semnat} disabled={saving} onChange={handleChange} />
      Contractul a fost semnat
    </label>
  );
}

/* ── ClientDetailsForm ───────────────────────────────────── */
function ClientDetailsForm({ editClient = null, onSaved, onCancel, onDeleted, showToast, proceduri = [] }) {
  const isEdit = !!editClient;
  const [tip, setTip] = useState(editClient?.tip || 'PF');

  const initFinanciar = () => {
    if (editClient?.financiar?.length) {
      let ctr = 100;
      return editClient.financiar.map(e => ({
        ...e,
        entryId: ++ctr,
        costuri: (e.costuri || []).map(c => ({ ...c, rowId: ++ctr })),
      }));
    }
    return [];
  };

  const [form, setForm] = useState(editClient ? {
    nume: editClient.nume || '', cnp: editClient.cnp || '', serie_bi: editClient.serie_bi || '',
    nr_bi: editClient.nr_bi || '', forma_juridica: editClient.forma_juridica || 'SRL',
    cui: editClient.cui || '', nr_orc: editClient.nr_orc || '', reprezentant: editClient.reprezentant || '',
    cnp_reprezentant: editClient.cnp_reprezentant || '', serie_reprezentant: editClient.serie_reprezentant || '',
    nr_reprezentant: editClient.nr_reprezentant || '', calitate_reprezentant: editClient.calitate_reprezentant || '',
    pachet_consultanta: editClient.pachet_consultanta || false,
    data_sfarsit_pachet: editClient.data_sfarsit_pachet || '',
    alocat_la: editClient.alocat_la || 'alienzaz',
    adresa: editClient.adresa || '', email: editClient.email || '', telefon: editClient.telefon || '',
    localitate: editClient.localitate || '', judet: editClient.judet || '', tara: editClient.tara || '',
    observatii: editClient.observatii || '',
    financiar: initFinanciar(),
  } : {
    nume: '', cnp: '', serie_bi: '', nr_bi: '', forma_juridica: 'SRL', cui: '', nr_orc: '', reprezentant: '',
    cnp_reprezentant: '', serie_reprezentant: '', nr_reprezentant: '', calitate_reprezentant: '',
    pachet_consultanta: false, data_sfarsit_pachet: '',
    alocat_la: 'alienzaz',
    adresa: '', email: '', telefon: '', localitate: '', judet: '', tara: '', observatii: '', financiar: [],
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());
  const toggleCollapsed = (id) => setCollapsed(s => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const [generatingDoc, setGeneratingDoc] = useState(null); // 'contract_WAP' | 'procura_WAP' | 'gdpr_WAP' | etc.
  const [procuraWAPModal, setProcuraWAPModal] = useState(null); // { company } when open
  const [addingEntry, setAddingEntry] = useState(false);
  const [docs, setDocs] = useState([]);
  const [pendingRows, setPendingRows] = useState([]);
  const idCounter = useRef(200);

  useEffect(() => {
    if (isEdit && editClient?.id) {
      listDocuments(editClient.id)
        .then(d => setDocs(d.documents || []))
        .catch(() => showToast('Eroare la incarcarea documentelor'));
    }
  }, [isEdit, editClient?.id, showToast]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const COMPANII = ['WAP', 'Cabinet', 'Inventa'];
  const companiiDisponibile = COMPANII.filter(c => !form.financiar.some(e => e.companie === c));

  const addEntry = (companie) => {
    idCounter.current += 1;
    setForm(f => ({ ...f, financiar: [...f.financiar, { entryId: idCounter.current, companie, valoare_contract: '', costuri: [] }] }));
    setAddingEntry(false);
  };
  const updateEntry = (entryId, patch) =>
    setForm(f => ({ ...f, financiar: f.financiar.map(e => e.entryId === entryId ? { ...e, ...patch } : e) }));

  const addEntryCost = (entryId) => {
    idCounter.current += 1;
    const rowId = idCounter.current;
    setForm(f => ({ ...f, financiar: f.financiar.map(e =>
      e.entryId === entryId ? { ...e, costuri: [...e.costuri, { rowId, procedura_id: '', tip: 'per_procedura', tipCustom: '', valoare: '', moneda: 'RON' }] } : e
    )}));
  };
  const updateEntryCost = (entryId, rowId, patch) =>
    setForm(f => ({ ...f, financiar: f.financiar.map(e =>
      e.entryId === entryId ? { ...e, costuri: e.costuri.map(c => c.rowId === rowId ? { ...c, ...patch } : c) } : e
    )}));
  const removeEntryCost = (entryId, rowId) =>
    setForm(f => ({ ...f, financiar: f.financiar.map(e =>
      e.entryId === entryId ? { ...e, costuri: e.costuri.filter(c => c.rowId !== rowId) } : e
    )}));

  const save = async () => {
    if (!form.nume.trim()) { showToast('Introduceti numele clientului'); return; }
    if (tip === 'PJ' && !form.cui.trim()) { showToast('CUI-ul este obligatoriu pentru PJ'); return; }
    // Adresa structurata — obligatorie pentru emiterea facturilor (SmartBill / e-Factura)
    const lipsa = [
      !form.adresa.trim() && 'Adresa',
      !form.localitate.trim() && 'Localitate',
      !form.judet.trim() && 'Judet',
      !form.tara.trim() && 'Tara',
    ].filter(Boolean);
    if (lipsa.length) { showToast(`Completeaza: ${lipsa.join(', ')}`); return; }
    setSaving(true);
    const payload = {
      ...form, tip,
      financiar: form.financiar.map(({ entryId, ...e }) => ({
        ...e, costuri: e.costuri.map(({ rowId, ...c }) => c),
      })),
    };
    try {
      if (isEdit) {
        const data = await updateClient(editClient.id, payload);
        showToast('✓ Client actualizat!');
        onSaved(data.client);
      } else {
        const data = await createClient(payload);
        showToast('✓ Client adaugat cu succes!');
        onSaved(data.client);
      }
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async (tip, company) => {
    // Procura WAP/Inventa: user picks the number each time — open modal
    if (tip === 'procura' && (company === 'WAP' || company === 'Inventa')) {
      setProcuraWAPModal({ company });
      return;
    }
    const key = `${tip}_${company}`;
    setGeneratingDoc(key);
    try {
      if (tip === 'contract') {
        await generateContract(editClient.id, company);
      } else {
        await generateDocument(editClient.id, tip, company);
      }
    } catch (err) {
      if (err?.missing?.length) {
        showToast(`Campuri lipsa: ${err.missing.join(', ')}`);
      } else {
        showToast(err?.message || 'Eroare la generarea documentului');
      }
    } finally {
      setGeneratingDoc(null);
    }
  };

  const addPendingRow = () => {
    idCounter.current += 1;
    setPendingRows(r => [...r, { rowId: idCounter.current, tip: '', tipCustom: '', uploading: false }]);
  };

  const updatePendingRow = (rowId, patch) => {
    setPendingRows(r => r.map(row => row.rowId === rowId ? { ...row, ...patch } : row));
  };

  const removePendingRow = (rowId) => {
    setPendingRows(r => r.filter(row => row.rowId !== rowId));
  };

  const handleFileSelect = async (rowId, file) => {
    if (!file) return;
    const row = pendingRows.find(r => r.rowId === rowId);
    if (!row) return;
    updatePendingRow(rowId, { uploading: true });
    try {
      const data = await uploadDocument(editClient.id, row.tip, row.tipCustom, file);
      setDocs(prev => [data.document, ...prev]);
      removePendingRow(rowId);
      showToast('✓ Document incarcat');
    } catch {
      showToast('Eroare la incarcarea documentului');
      updatePendingRow(rowId, { uploading: false });
    }
  };

  const handleDocDelete = async (docId) => {
    try {
      await deleteDocument(docId);
      setDocs(prev => prev.filter(d => d.id !== docId));
      showToast('Document sters');
    } catch {
      showToast('Eroare la stergere');
    }
  };

  return (
    <>
      <div className="type-tabs">
        <div className={`type-tab${tip === 'PF' ? ' tt-active' : ''}`} onClick={() => setTip('PF')}>
          <div className="tt-title">👤 Persoana Fizica</div>
          <div className="tt-sub">CNP, serie si nr. buletin</div>
        </div>
        <div className={`type-tab${tip === 'PJ' ? ' tt-active' : ''}`} onClick={() => setTip('PJ')}>
          <div className="tt-title">🏢 Persoana Juridica</div>
          <div className="tt-sub">SRL, SA, ONG, PFA, Asociatie</div>
        </div>
      </div>

      {tip === 'PF' && (
        <div>
          <div className="section-label">Date personale</div>
          <div className="f-row mb10">
            <div className="field"><label className="req">Nume si prenume</label><input type="text" value={form.nume} onChange={e => set('nume', e.target.value)} placeholder="ex: Ion Popescu" /></div>
            <div className="field"><label className="req">CNP</label><input type="text" value={form.cnp} onChange={e => set('cnp', e.target.value)} placeholder="CNP / identificator" maxLength={50} /></div>
          </div>
          <div className="f-row mb10">
            <div className="field"><label>Serie buletin</label><input type="text" value={form.serie_bi} onChange={e => set('serie_bi', e.target.value.toUpperCase())} placeholder="ex: RX" maxLength={2} style={{ textTransform: 'uppercase' }} /></div>
            <div className="field"><label>Nr. buletin</label><input type="text" value={form.nr_bi} onChange={e => set('nr_bi', e.target.value)} placeholder="ex: 456789" /></div>
          </div>
          <div className="field mb10"><label className="req">Adresa</label><input type="text" value={form.adresa} onChange={e => set('adresa', e.target.value)} placeholder="Strada, nr., bloc, ap." /></div>
          <div className="f-row f-row-3 mb10">
            <div className="field"><label className="req">Localitate</label><input type="text" value={form.localitate} onChange={e => set('localitate', e.target.value)} placeholder="ex: Cluj-Napoca / Sector 1" /></div>
            <div className="field">
              <label className="req">Judet / Regiune</label>
              <input type="text" list="judete-list" value={form.judet} onChange={e => set('judet', e.target.value)} placeholder="ex: Cluj (sau regiunea, pt. strainatate)" />
              <datalist id="judete-list">
                {JUDETE.map(j => <option key={j} value={j} />)}
              </datalist>
            </div>
            <div className="field"><label className="req">Tara</label><input type="text" value={form.tara} onChange={e => set('tara', e.target.value)} placeholder="ex: Romania" /></div>
          </div>
        </div>
      )}

      {tip === 'PJ' && (
        <div>
          <div className="section-label">Date companie</div>
          <div className="f-row mb10">
            <div className="field"><label className="req">Denumire companie</label><input type="text" value={form.nume} onChange={e => set('nume', e.target.value)} placeholder="ex: TechCorp Solutions" /></div>
            <div className="field">
              <label className="req">Forma juridica</label>
              <FilterPill value={form.forma_juridica} onChange={e => set('forma_juridica', e.target.value)}>
                <option value="">-- Selecteaza --</option>
                {['SRL', 'SA', 'ONG', 'Asociatie', 'PFA', 'RA'].map(o => <option key={o} value={o}>{o}</option>)}
              </FilterPill>
            </div>
          </div>
          <div className="f-row mb10">
            <div className="field"><label className="req">CUI / Cod fiscal</label><input type="text" value={form.cui} onChange={e => set('cui', e.target.value)} placeholder="ex: RO12345678" /></div>
            <div className="field"><label>Nr. inregistrare ORC</label><input type="text" value={form.nr_orc} onChange={e => set('nr_orc', e.target.value)} placeholder="ex: J40/1234/2020" /></div>
          </div>
          <div className="field mb10"><label className="req">Adresa sediu social</label><input type="text" value={form.adresa} onChange={e => set('adresa', e.target.value)} placeholder="Strada, nr., bloc, ap." /></div>
          <div className="f-row f-row-3 mb10">
            <div className="field"><label className="req">Localitate</label><input type="text" value={form.localitate} onChange={e => set('localitate', e.target.value)} placeholder="ex: Cluj-Napoca / Sector 1" /></div>
            <div className="field">
              <label className="req">Judet / Regiune</label>
              <input type="text" list="judete-list" value={form.judet} onChange={e => set('judet', e.target.value)} placeholder="ex: Cluj (sau regiunea, pt. strainatate)" />
              <datalist id="judete-list">
                {JUDETE.map(j => <option key={j} value={j} />)}
              </datalist>
            </div>
            <div className="field"><label className="req">Tara</label><input type="text" value={form.tara} onChange={e => set('tara', e.target.value)} placeholder="ex: Romania" /></div>
          </div>
          <div className="field mb10"><label>Reprezentant legal</label><input type="text" value={form.reprezentant} onChange={e => set('reprezentant', e.target.value)} placeholder="Nume si prenume" /></div>
          <div className="f-row mb10">
            <div className="field"><label>CNP reprezentant</label><input type="text" value={form.cnp_reprezentant} onChange={e => set('cnp_reprezentant', e.target.value)} placeholder="13 cifre" maxLength={13} /></div>
            <div className="field"><label>Serie CI</label><input type="text" value={form.serie_reprezentant} onChange={e => set('serie_reprezentant', e.target.value.toUpperCase())} placeholder="ex: RX" maxLength={2} style={{ textTransform: 'uppercase' }} /></div>
            <div className="field"><label>Nr. CI</label><input type="text" value={form.nr_reprezentant} onChange={e => set('nr_reprezentant', e.target.value)} placeholder="ex: 123456" maxLength={6} /></div>
          </div>
          <div className="field mb10">
            <label>Calitatea reprezentantului</label>
            <FilterPill value={form.calitate_reprezentant} onChange={e => set('calitate_reprezentant', e.target.value)}>
              <option value="">-- Selecteaza --</option>
              <option value="Administrator">Administrator</option>
              <option value="Director General">Director General</option>
              <option value="Imputernicit">Imputernicit</option>
            </FilterPill>
          </div>
        </div>
      )}

      <div className="acp-divider" />
      <div className="section-label">Consultanta &amp; alocare</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
          <input type="checkbox" checked={form.pachet_consultanta} onChange={e => set('pachet_consultanta', e.target.checked)} />
          Pachet de consultanta
        </label>
        {form.pachet_consultanta && (
          <div className="field" style={{ margin: 0 }}>
            <label style={{ fontSize: 12 }}>Data sfarsit pachet</label>
            <DateField value={form.data_sfarsit_pachet} onChange={v => set('data_sfarsit_pachet', v)} />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
        <span style={{ fontSize: 13, color: '#6b7280' }}>Alocat la</span>
        <FilterPill value={form.alocat_la} onChange={e => set('alocat_la', e.target.value)}>
          <option value="">— Societate —</option>
          <option value="alienzaz">Alienzaz/Cabinet</option>
          <option value="inventa">Inventa</option>
        </FilterPill>
      </div>

      <div className="acp-divider" />
      <div className="section-label">Date contact</div>
      <div className="f-row mb10">
        <div className="field"><label className="req">E-mail / corespondenta</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@domeniu.ro" /></div>
        <div className="field"><label>Nr. telefon</label><input type="tel" value={form.telefon} onChange={e => set('telefon', e.target.value)} placeholder="07XX XXX XXX" /></div>
      </div>
      <div className="field mb10"><label>Observatii</label><textarea value={form.observatii} onChange={e => set('observatii', e.target.value)} placeholder="Note interne despre client..." style={{ minHeight: 66 }} /></div>

      <div className="acp-divider" />
      <div className="doc-section-head" style={{ marginBottom: 12 }}>
        <div className="section-label" style={{ margin: 0 }}>Date financiare</div>
        {companiiDisponibile.length > 0 && !addingEntry && (
          <button className="btn btn-secondary btn-sm" type="button" onClick={() => setAddingEntry(true)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Adauga
          </button>
        )}
      </div>

      {addingEntry && (
        <div className="fin-company-picker">
          <span className="fin-picker-label">Selecteaza compania:</span>
          {companiiDisponibile.map(c => (
            <button key={c} className="btn btn-secondary btn-sm fin-company-btn" type="button" onClick={() => addEntry(c)}>
              {c}
            </button>
          ))}
          <button className="btn btn-ghost btn-xs" type="button" onClick={() => setAddingEntry(false)}>Anuleaza</button>
        </div>
      )}

      {form.financiar.length === 0 && !addingEntry && (
        <div className="doc-empty">Nicio intrare financiara adaugata inca.</div>
      )}

      {form.financiar.map(entry => {
        const isCollapsed = collapsed.has(entry.entryId);
        return (
          <div key={entry.entryId} className="fin-entry">
            <div className="fin-entry-head" style={{ cursor: 'pointer' }} onClick={() => toggleCollapsed(entry.entryId)}>
              <span className="fin-company-badge">{entry.companie}</span>
              <svg
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                style={{ transition: 'transform .18s', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', color: 'var(--muted)', flexShrink: 0 }}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>

            {!isCollapsed && <>
              <div className="field mb10">
                <label>Valoare contract</label>
                <div className="input-suffix-wrap">
                  <input type="number" min="0" step="0.01" value={entry.valoare_contract} onChange={e => updateEntry(entry.entryId, { valoare_contract: e.target.value })} placeholder="0" />
                  <span className="input-suffix">Ron</span>
                </div>
              </div>

              <div className="doc-section-head mb10">
                <span className="fin-sub-label">Costuri</span>
                <button className="btn btn-secondary btn-sm" type="button" onClick={() => addEntryCost(entry.entryId)}>+ Adauga cost</button>
              </div>

              {entry.costuri.map(cost => (
                <div key={cost.rowId} className="cost-row">
                  <FilterPill searchable className="cost-proc" value={cost.procedura_id} onChange={e => updateEntryCost(entry.entryId, cost.rowId, { procedura_id: e.target.value })}>
                    <option value="">— Procedura —</option>
                    {proceduri.map(p => (
                      <option key={p.id} value={p.id}>
                        {TIP_PROC_LABEL[p.tip_proc] || p.tip_proc}{p.data?.numar ? ` · ${p.data.numar}` : p.data?.numarDepozit ? ` · ${p.data.numarDepozit}` : p.data?.denumire ? ` · ${p.data.denumire}` : ''}
                      </option>
                    ))}
                  </FilterPill>
                  <FilterPill className="cost-tip" value={cost.tip} onChange={e => updateEntryCost(entry.entryId, cost.rowId, { tip: e.target.value, tipCustom: '' })}>
                    <option value="">— Tip cost —</option>
                    <option value="per_procedura">Per procedura</option>
                    <option value="per_ora">Per ora</option>
                    <option value="altul">Altul</option>
                  </FilterPill>
                  {cost.tip === 'altul' && (
                    <input className="cost-custom" type="text" placeholder="Tip cost..." value={cost.tipCustom} onChange={e => updateEntryCost(entry.entryId, cost.rowId, { tipCustom: e.target.value })} />
                  )}
                  <input className="cost-val" type="number" min="0" step="0.01" placeholder="0" value={cost.valoare} onChange={e => updateEntryCost(entry.entryId, cost.rowId, { valoare: e.target.value })} />
                  <FilterPill className="cost-moneda" value={cost.moneda || 'RON'} onChange={e => updateEntryCost(entry.entryId, cost.rowId, { moneda: e.target.value })}>
                    <option value="">—</option>
                    {MONEDE.map(m => <option key={m} value={m}>{m}</option>)}
                  </FilterPill>
                  <button className="btn btn-ghost btn-xs" type="button" onClick={() => removeEntryCost(entry.entryId, cost.rowId)}>✕</button>
                </div>
              ))}
              {entry.costuri.length === 0 && <div className="doc-empty" style={{ marginBottom: 8 }}>Niciun cost adaugat.</div>}

              <div className="generare-gdpr" style={{ marginTop: 12 }}>
                {[
                  { tip: 'contract', label: 'Contract', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
                  { tip: 'procura',  label: 'Procura',  icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 15 11 17 15 13"/></svg> },
                  { tip: 'gdpr',     label: 'GDPR',     icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
                ].filter(({ tip }) => !(tip === 'procura' && entry.companie === 'Cabinet'))
                 .map(({ tip, label, icon }) => {
                  const key = `${tip}_${entry.companie}`;
                  const busy = generatingDoc === key;
                  return (
                    <button key={tip} className="btn btn-secondary btn-md" type="button" disabled={busy} onClick={() => handleGenerate(tip, entry.companie)}>
                      {icon}
                      {busy ? 'Se genereaza...' : `${label} ${entry.companie}`}
                    </button>
                  );
                })}
              </div>
              {/* Semnat checkbox — shown only if a contract exists for this company */}
              {(() => {
                const contract = (editClient.contracte || []).find(c => c.company === entry.companie);
                if (!contract) return null;
                return (
                  <ContractSemnatCheckbox
                    key={contract.id}
                    contract={contract}
                    clientId={editClient.id}
                    showToast={showToast}
                  />
                );
              })()}
            </>}
          </div>
        );
      })}

      <div className="acp-actions" style={{ marginTop: 16 }}>
        <button className="btn btn-primary" type="button" onClick={save} disabled={saving}>{saving ? 'Se salveaza...' : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> {isEdit ? 'Salveaza modificarile' : 'Salveaza Client'}</>}</button>
        {onCancel && <button className="btn btn-ghost btn-sm" type="button" onClick={onCancel} style={{ marginLeft: 'auto' }}>Anuleaza</button>}
      </div>

      {isEdit && (
        <>
          <div className="acp-divider" />
          <div className="doc-section-head">
            <div className="section-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
              Documente
            </div>
            <button className="btn btn-secondary btn-sm" type="button" onClick={addPendingRow}>+ Adauga document</button>
          </div>

          {pendingRows.map(row => {
            const canUpload = row.tip && (row.tip !== 'altele' || row.tipCustom.trim());
            return (
              <div key={row.rowId} className="doc-pending-row">
                <FilterPill
                  value={row.tip}
                  onChange={e => updatePendingRow(row.rowId, { tip: e.target.value, tipCustom: '' })}
                >
                  <option value="">-- Tip document --</option>
                  <option value="copie_buletin">Copie Buletin</option>
                  <option value="copie_cui">Copie CUI</option>
                  <option value="copie_contract">Copie Contract</option>
                  <option value="copie_procura">Copie Procura</option>
                  <option value="declaratie_sme">Declaratie SME</option>
                  <option value="altele">Altele</option>
                </FilterPill>
                {row.tip === 'altele' && (
                  <input
                    className="doc-custom-input"
                    type="text"
                    placeholder="Denumire document..."
                    value={row.tipCustom}
                    onChange={e => updatePendingRow(row.rowId, { tipCustom: e.target.value })}
                  />
                )}
                <label className={`btn btn-secondary btn-xs doc-file-btn${!canUpload || row.uploading ? ' disabled' : ''}`}>
                  {row.uploading ? 'Se incarca...' : 'Alege fisier'}
                  <input
                    type="file"
                    style={{ display: 'none' }}
                    disabled={!canUpload || row.uploading}
                    onChange={e => handleFileSelect(row.rowId, e.target.files[0])}
                  />
                </label>
                <button className="btn btn-ghost btn-xs" type="button" onClick={() => removePendingRow(row.rowId)}>✕</button>
              </div>
            );
          })}

          {docs.length === 0 && pendingRows.length === 0 && (
            <div className="doc-empty">Niciun document adaugat inca.</div>
          )}

          {docs.map(doc => (
            <DocItem key={doc.id} doc={doc} onDelete={handleDocDelete} />
          ))}

          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <button
              type="button"
              className="btn btn-sm"
              style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }}
              disabled={deleting}
              onClick={async () => {
                if (!window.confirm(`Stergi clientul "${editClient.nume}" si toate datele asociate? Aceasta actiune nu poate fi anulata.`)) return;
                setDeleting(true);
                try {
                  await deleteClient(editClient.id);
                  onDeleted(editClient.id);
                } catch {
                  showToast('Eroare la stergere client');
                  setDeleting(false);
                }
              }}
            >
              {deleting ? 'Se sterge...' : 'Sterge client'}
            </button>
          </div>
        </>
      )}

      {procuraWAPModal && (
        <ProcuraWAPModal
          clientId={editClient.id}
          company={procuraWAPModal.company}
          onClose={() => setProcuraWAPModal(null)}
          showToast={showToast}
        />
      )}
    </>
  );
}

/* ── ProcuraWAPModal ─────────────────────────────────────── */
function ProcuraWAPModal({ clientId, company, onClose, showToast }) {
  const [procuri, setProcuri]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [nr, setNr]               = useState('');
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    listGeneratedDocs(clientId, 'procura', company)
      .then(r => setProcuri(r.data.docs || []))
      .catch(() => showToast('Eroare la incarcarea procurilor'))
      .finally(() => setLoading(false));
  }, [clientId, company]); // eslint-disable-line

  const handleGenerate = async () => {
    if (!nr.trim()) { showToast('Introduceti numarul procurii'); return; }
    setGenerating(true);
    try {
      await generateDocument(clientId, 'procura', company, nr.trim());
      // Refresh list
      const r = await listGeneratedDocs(clientId, 'procura', company);
      setProcuri(r.data.docs || []);
      setNr('');
      showToast('✓ Procura generata');
    } catch (err) {
      if (err?.missing?.length) showToast(`Campuri lipsa: ${err.missing.join(', ')}`);
      else showToast(err?.message || 'Eroare la generare');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (doc) => {
    if (downloading === doc.id) return;
    setDownloading(doc.id);
    try {
      await redownloadDocument(clientId, doc.id);
    } catch {
      showToast('Eroare la descarcare');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box fm-modal fm-modal--sm">
        {/* Header */}
        <div className="modal-sticky-header fm-header">
          <div className="fm-title-row">
            <span className="fm-title">Procuri {company}</span>
            <button className="icon-circle-btn" onClick={onClose} title="Inchide">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-scroll-body">
          {/* Existing procuri list */}
          {loading && <div className="doc-empty">Se incarca...</div>}

          {!loading && procuri.length > 0 && (
            <>
              <div className="section-label">Procuri existente</div>
              {procuri.map(doc => (
                <div key={doc.id} className="procura-row">
                  <div className="procura-row-info">
                    <span className="procura-nr">Nr. {doc.nr || '—'}</span>
                    <span className="procura-date">{fmtDateRO(doc.data_doc)}</span>
                  </div>
                  <button
                    type="button"
                    className="icon-circle-btn"
                    title="Descarca PDF"
                    disabled={downloading === doc.id}
                    onClick={() => handleDownload(doc)}
                  >
                    {downloading === doc.id
                      ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" strokeDasharray="31.4" strokeDashoffset="10"/></svg>
                      : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    }
                  </button>
                </div>
              ))}
            </>
          )}

          {/* New procura form */}
          <div className="section-label" style={{ marginTop: procuri.length ? 20 : 0 }}>
            {procuri.length === 0 && !loading ? 'Nicio procura generata inca — adauga prima:' : 'Procura noua'}
          </div>
          <div className="field">
            <label>Numar procura</label>
            <input
              type="text"
              placeholder="ex: 42"
              value={nr}
              onChange={e => setNr(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGenerate()}
              autoFocus
            />
          </div>
        </div>

        {/* Footer */}
        <div className="fm-footer">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Inchide</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={generating || !nr.trim()}
            onClick={handleGenerate}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 15 11 17 15 13"/></svg>
            {generating ? 'Se genereaza...' : 'Genereaza procura'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── AddClientPanel (inline "create new client" panel) ───── */
function AddClientPanel({ onClose, onSaved, showToast }) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (panelRef.current) panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  return (
    <div className="add-client-panel" ref={panelRef}>
      <div className="acp-header">
        <div>
          <div className="acp-title">Client Nou</div>
          <div className="acp-sub">Completati informatiile noului client</div>
        </div>
        <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>✕ Inchide</button>
      </div>
      <div className="acp-body">
        <ClientDetailsForm onSaved={onSaved} onCancel={onClose} showToast={showToast} />
      </div>
    </div>
  );
}

function DocItem({ doc, onDelete }) {
  const [confirmDel, setConfirmDel] = useState(false);
  const label = doc.tip === 'altele' ? doc.tip_custom : TIP_DOC_LABEL[doc.tip] || doc.tip;

  return (
    <div className="doc-saved-row">
      <div className="doc-saved-info">
        <span className="doc-saved-type">{label}</span>
        <span className="doc-saved-name">{doc.filename}</span>
      </div>
      <div className="doc-saved-actions">
        <a
          className="btn btn-ghost btn-xs"
          href={getMediaUrl(doc.url)}
          download={doc.filename}
          target="_blank"
          rel="noreferrer"
        >
          ↓ Descarca
        </a>
        {confirmDel ? (
          <>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>Stergi?</span>
            <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444' }} onClick={() => onDelete(doc.id)}>Da</button>
            <button className="btn btn-ghost btn-xs" onClick={() => setConfirmDel(false)}>Nu</button>
          </>
        ) : (
          <button className="btn btn-ghost btn-xs" onClick={() => setConfirmDel(true)}>✕</button>
        )}
      </div>
    </div>
  );
}

/* ── CheltuialaModal ─────────────────────────────────────── */
const CHELTUIALA_MONEDE = ['EUR', 'USD', 'GBP', 'CHF', 'RON', 'JPY', 'CAD', 'AUD'];
const OFICII_COMMON = ['USPTO', 'UKIPO', 'EUIPO', 'OSIM', 'WIPO', 'INPI', 'DPMA', 'JPO', 'CIPO', 'IP Australia'];

function CheltuialaModal({ client, proceduri, item, oficiiKnown, onClose, onSaved, showToast }) {
  const isEdit = Boolean(item?.id);
  const [form, setForm] = useState({
    obiect:        item?.obiect        || '',
    cost:          item?.cost          || '',
    moneda:        item?.moneda        || 'EUR',
    oficiu:        item?.oficiu        || '',
    data_emitere:  item?.data_emitere  || '',
    data_achitare: item?.data_achitare || '',
    achitat:       item?.achitat       || false,
    procedura_id:  item?.procedura_id  || '',
  });
  const [saving, setSaving]           = useState(false);
  const [existingDocs, setExistingDocs] = useState(item?.documente || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const fileInputRef = useRef(null);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const oficiiSuggestions = [...new Set([...OFICII_COMMON, ...oficiiKnown])].sort();

  const addPendingFile = (file) => setPendingFiles(prev => [...prev, { file, desc: '' }]);
  const setPendingDesc = (idx, desc) => setPendingFiles(prev => prev.map((p, i) => i === idx ? { ...p, desc } : p));
  const removePending  = (idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx));

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Stergi documentul?')) return;
    setDeletingDocId(docId);
    try {
      await deleteCheltuialaFile(docId);
      setExistingDocs(prev => prev.filter(d => d.id !== docId));
    } catch {
      showToast('Eroare la stergere');
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleSave = async () => {
    if (!form.obiect.trim()) return showToast('Completati obiectul');
    if (!form.cost || isNaN(Number(form.cost))) return showToast('Completati costul');
    if (!form.oficiu.trim()) return showToast('Completati oficiul');
    setSaving(true);
    try {
      const payload = {
        obiect:        form.obiect.trim(),
        cost:          Number(form.cost),
        moneda:        form.moneda,
        oficiu:        form.oficiu.trim(),
        data_emitere:  form.data_emitere  || null,
        data_achitare: form.data_achitare || null,
        achitat:       form.achitat,
        procedura_id:  form.procedura_id  || null,
      };
      let result;
      if (isEdit) {
        result = await patchCheltuiala(item.id, payload);
      } else {
        result = await createCheltuiala(client.id, payload);
      }
      const saved = result.cheltuiala;
      for (const p of pendingFiles) {
        const fd = new FormData();
        fd.append('file', p.file);
        fd.append('denumire', p.desc);
        try {
          const r = await uploadCheltuialaFile(saved.id, fd);
          saved.documente = [...(saved.documente || []), r.document];
        } catch { /* skip failed uploads silently */ }
      }
      onSaved(saved, isEdit);
      onClose();
    } catch {
      showToast('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const procLabel = (p) => {
    const tip = TIP_PROC_LABEL[p.tip_proc] || p.tip_proc;
    const nr = p.data?.numar || p.data?.numarDepozit || p.data?.denumire || '';
    return nr ? `${tip} · ${nr}` : tip;
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box fm-modal">
        <div className="modal-sticky-header fm-header">
          <div className="fm-title-row">
            <span className="fm-title">{isEdit ? 'Editeaza factura internationala' : 'Factura internationala noua'}</span>
            <button className="icon-circle-btn" onClick={onClose} title="Inchide">✕</button>
          </div>
        </div>

        <div className="modal-scroll-body">
          <div className="f-row">
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label className="req">Obiect</label>
              <input
                type="text"
                value={form.obiect}
                placeholder="Ex: Taxa depunere cerere marca"
                onChange={e => set({ obiect: e.target.value })}
              />
            </div>
          </div>

          <div className="f-row f-row-3">
            <div className="field">
              <label className="req">Cost</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.cost}
                onChange={e => set({ cost: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Moneda</label>
              <select value={form.moneda} onChange={e => set({ moneda: e.target.value })}>
                {CHELTUIALA_MONEDE.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="req">Oficiu</label>
              <input
                type="text"
                list="oficii-suggestions"
                value={form.oficiu}
                placeholder="Ex: USPTO"
                onChange={e => set({ oficiu: e.target.value })}
              />
              <datalist id="oficii-suggestions">
                {oficiiSuggestions.map(o => <option key={o} value={o} />)}
              </datalist>
            </div>
          </div>

          <div className="f-row f-row-3">
            <div className="field">
              <label>Data emitere</label>
              <DateField value={form.data_emitere} onChange={v => set({ data_emitere: v })} />
            </div>
            <div className="field">
              <label>Data achitare</label>
              <DateField value={form.data_achitare} onChange={v => set({ data_achitare: v })} />
            </div>
            <div className="field" style={{ justifyContent: 'flex-end', paddingBottom: 2 }}>
              <label>Status plata</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={form.achitat}
                  onChange={e => set({ achitat: e.target.checked })}
                />
                Achitat
              </label>
            </div>
          </div>

          <div className="f-row">
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Procedura asociata <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional)</span></label>
              <select value={form.procedura_id} onChange={e => set({ procedura_id: e.target.value })}>
                <option value="">— Fara procedura —</option>
                {(proceduri || []).map(p => (
                  <option key={p.id} value={String(p.id)}>{procLabel(p)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Documents ── */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Documente</div>

            {existingDocs.map(d => (
              <div key={d.id} className="doc-saved-row">
                <div className="doc-saved-info">
                  {d.denumire && <span className="doc-saved-type">{d.denumire}</span>}
                  <a className="doc-saved-name" href={getMediaUrl(d.url)} target="_blank" rel="noreferrer">{d.filename}</a>
                </div>
                <div className="doc-saved-actions">
                  <button
                    type="button" className="btn btn-ghost btn-xs"
                    style={{ color: '#ef4444' }}
                    disabled={deletingDocId === d.id}
                    onClick={() => handleDeleteDoc(d.id)}
                  >{deletingDocId === d.id ? '…' : 'Sterge'}</button>
                </div>
              </div>
            ))}

            {pendingFiles.map((p, idx) => (
              <div key={idx} className="doc-pending-row">
                <input
                  className="doc-custom-input"
                  type="text"
                  placeholder="Nume / descriere document"
                  value={p.desc}
                  onChange={e => setPendingDesc(idx, e.target.value)}
                />
                <span style={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{p.file.name}</span>
                <button type="button" className="btn btn-ghost btn-xs" style={{ color: '#ef4444' }} onClick={() => removePending(idx)}>✕</button>
              </div>
            ))}

            <button
              type="button"
              className="btn btn-secondary btn-sm"
              style={{ alignSelf: 'flex-start', marginTop: 4 }}
              onClick={() => fileInputRef.current?.click()}
            >+ Adauga document</button>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files[0]) { addPendingFile(e.target.files[0]); e.target.value = ''; } }}
            />
          </div>
        </div>

        <div className="fm-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Renunta</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Se salveaza...' : isEdit ? 'Salveaza modificarile' : 'Adauga factura'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── InternationalTab ────────────────────────────────────── */
function InternationalTab({ client, proceduri, showToast }) {
  const [cheltuieli, setCheltuieli]   = useState([]);
  const [oficiiKnown, setOficiiKnown] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modalItem, setModalItem]     = useState(undefined); // undefined=closed, null=new, obj=edit
  const [deletingId, setDeletingId]   = useState(null);

  useEffect(() => {
    setLoading(true);
    listCheltuieli(client.id)
      .then(r => {
        setCheltuieli(r.cheltuieli || []);
        setOficiiKnown(r.oficii_known || []);
      })
      .catch(() => showToast('Eroare la incarcare'))
      .finally(() => setLoading(false));
  }, [client.id, showToast]);

  const handleSaved = (c, isEdit) => {
    setCheltuieli(prev => isEdit ? prev.map(x => x.id === c.id ? c : x) : [c, ...prev]);
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Stergi factura internationala "${c.obiect}"?`)) return;
    setDeletingId(c.id);
    try {
      await deleteCheltuiala(c.id);
      setCheltuieli(prev => prev.filter(x => x.id !== c.id));
    } catch {
      showToast('Eroare la stergere');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleAchitat = async (c) => {
    try {
      const res = await patchCheltuiala(c.id, { achitat: !c.achitat });
      setCheltuieli(prev => prev.map(x => x.id === c.id ? res.cheltuiala : x));
    } catch {
      showToast('Eroare la actualizare');
    }
  };

  const fmtCost = (c) => `${parseFloat(c.cost).toFixed(2)} ${c.moneda}`;

  return (
    <div>
      <div className="fin-toolbar">
        <span />
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setModalItem(null)}>
          + Factura internationala noua
        </button>
      </div>

      {loading ? (
        <div className="fin-empty-state"><p>Se incarca...</p></div>
      ) : cheltuieli.length === 0 ? (
        <div className="fin-empty-state">
          <span className="fin-empty-icon">🌐</span>
          <p>Nicio factura internationala inregistrata.</p>
        </div>
      ) : cheltuieli.map(c => (
        <div key={c.id} className="fin-doc-row" onClick={() => setModalItem(c)} style={{ cursor: 'pointer' }}>
          <div className="fin-doc-main">
            <span className="fin-doc-badge" style={{ background: '#e0f2fe', color: '#0369a1' }}>{c.oficiu}</span>
            <span className="fin-doc-nr">{c.obiect}</span>
            {c.data_emitere && <span className="fin-doc-date">{fmtDate(c.data_emitere)}</span>}
          </div>
          <div className="fin-doc-right" onClick={e => e.stopPropagation()}>
            <span className="fin-doc-total">{fmtCost(c)}</span>
            <button
              type="button"
              className="btn btn-sm"
              style={c.achitat
                ? { background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' }
                : { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}
              onClick={() => handleToggleAchitat(c)}
            >
              {c.achitat ? 'Achitat' : 'Neachitat'}
            </button>
            <button
              type="button"
              className="icon-circle-btn"
              style={{ color: '#ef4444' }}
              disabled={deletingId === c.id}
              title="Sterge"
              onClick={() => handleDelete(c)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
              </svg>
            </button>
          </div>
        </div>
      ))}

      {modalItem !== undefined && (
        <CheltuialaModal
          client={client}
          proceduri={proceduri}
          item={modalItem}
          oficiiKnown={oficiiKnown}
          onClose={() => setModalItem(undefined)}
          onSaved={handleSaved}
          showToast={showToast}
        />
      )}
    </div>
  );
}

/* ── FinanciarTab helpers ─────────────────────────────────── */

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtMoney(v, moneda) {
  if (v === null || v === undefined) return '—';
  return `${parseFloat(v).toFixed(2)} ${moneda || 'RON'}`;
}


/* ── DocRow ───────────────────────────────────────────────── */
function DocRow({ doc, onConvert, onStatusChange, onEdit, showToast }) {
  const isFiscala = doc.tip === 'fiscala';
  const [changing, setChanging]       = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleStatus = async (newStatus) => {
    setChanging(true);
    try {
      await onStatusChange(doc.id, newStatus);
    } finally {
      setChanging(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!doc.numar) return;
    setDownloading(true);
    try {
      const res = await smartbillSvc.downloadPdf(doc.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.serie}-${doc.numar}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Eroare la descarcare PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fin-doc-row" onClick={() => onEdit(doc)} style={{ cursor: 'pointer' }}>
      <div className="fin-doc-main">
        <span className="fin-doc-badge fin-doc-badge--tip">{isFiscala ? 'Fiscala' : 'Proforma'}</span>
        <span className="fin-doc-nr">{doc.serie}-{doc.numar || '—'}</span>
        <span className="fin-doc-date">{fmtDate(doc.data_emitere)}</span>
        <span className={`fin-doc-badge fin-doc-badge--svc fin-doc-badge--${doc.tip_serviciu}`}>{SVC_LABEL[doc.tip_serviciu] || doc.tip_serviciu}</span>
        {doc.proforma_ref_numar && (
          <span className="fin-doc-ref">din {doc.proforma_ref_serie}-{doc.proforma_ref_numar}</span>
        )}
      </div>
      <div className="fin-doc-right" onClick={e => e.stopPropagation()}>
        <span className="fin-doc-total">{fmtMoney(doc.total, doc.moneda)}</span>
        <FilterPill
          value={doc.status_plata}
          onChange={e => handleStatus(e.target.value)}
          disabled={changing}
        >
          <option value="">— Status —</option>
          <option value="neplatita">Neplatita</option>
          <option value="platita">Platita</option>
          <option value="facturata">Facturata</option>
          <option value="facturata_partial">Facturata partial</option>
          <option value="depasita">Depasita</option>
          <option value="anulata">Anulata</option>
          <option value="stornata">Stornata</option>
        </FilterPill>
        {doc.numar && (
          <button type="button" className="icon-circle-btn" onClick={handleDownloadPdf} disabled={downloading} title="Descarca PDF">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        )}
        {!isFiscala && doc.numar && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onConvert(doc)}>
            → Fiscala
          </button>
        )}
      </div>
    </div>
  );
}

/* ── FinanciarTab ────────────────────────────────────────── */
/* ── TrimiteFacturiModal ─────────────────────────────────── */
const EXCLUDE_STATUS = new Set(['platita', 'anulata', 'stornata']);

function TrimiteFacturiModal({ client, documente, onClose, showToast }) {
  const neachitate = documente
    .filter(d => !EXCLUDE_STATUS.has(d.status_plata) && d.numar)
    .sort((a, b) => (b.data_emitere || '').localeCompare(a.data_emitere || ''));

  const [selected, setSelected]   = useState(() => new Set(neachitate.map(d => d.id)));
  const [sending, setSending]     = useState(false);
  const [sentInfo, setSentInfo]   = useState(null);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(prev =>
    prev.size === neachitate.length ? new Set() : new Set(neachitate.map(d => d.id))
  );

  const handleSend = async () => {
    if (selected.size === 0) return showToast('Selectati cel putin o factura');
    if (!(client.email || '').trim()) {
      return showToast('Clientul nu are adresa de email configurata. Adaugati-o mai intai in profilul clientului.');
    }
    setSending(true);
    try {
      const res = await sendFacturiEmail(client.id, { doc_ids: [...selected] });
      setSentInfo(res);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Eroare la trimitere email';
      showToast(msg);
    } finally {
      setSending(false);
    }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    const [y, m, z] = d.split('-');
    return `${z}.${m}.${y}`;
  };

  if (sentInfo) {
    return (
      <div className="modal-overlay">
        <div className="modal-box" style={{ maxWidth: 440 }}>
          <div className="modal-sticky-header fm-header">
            <div className="fm-title-row">
              <span className="fm-title">Email trimis</span>
              <button className="icon-circle-btn" onClick={onClose}>✕</button>
            </div>
          </div>
          <div className="modal-scroll-body" style={{ padding: '24px 24px 8px' }}>
            <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 10, padding: '14px 16px', color: '#065f46', fontSize: 14, marginBottom: 16 }}>
              ✓ Email trimis catre <strong>{sentInfo.sent_to?.join(', ')}</strong>
            </div>
            {sentInfo.failed_pdfs?.length > 0 && (
              <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px', color: '#92400e', fontSize: 13 }}>
                Atentie: PDF-urile urmatoare nu au putut fi atasate: {sentInfo.failed_pdfs.join(', ')}
              </div>
            )}
          </div>
          <div className="fm-footer">
            <button className="btn btn-primary" onClick={onClose}>Inchide</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 600 }}>
        <div className="modal-sticky-header fm-header">
          <div className="fm-title-row">
            <span className="fm-title">Trimite facturi</span>
            <button className="icon-circle-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-scroll-body" style={{ padding: '20px 24px 8px' }}>
          {!(client.email || '').trim() && (
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px', color: '#92400e', fontSize: 13, marginBottom: 16 }}>
              ⚠ Clientul nu are adresa de email. Adaugati-o in profilul clientului inainte de a trimite.
            </div>
          )}
          {neachitate.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: 14 }}>Nu exista facturi neachitate cu numar emis.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8faff' }}>
                  <th style={{ width: 36, padding: '8px 10px', textAlign: 'center' }}>
                    <input type="checkbox" checked={selected.size === neachitate.length && neachitate.length > 0} onChange={toggleAll} />
                  </th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px', color: '#445b8f', borderBottom: '2px solid #e8edff' }}>Tip</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px', color: '#445b8f', borderBottom: '2px solid #e8edff' }}>Numar</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px', color: '#445b8f', borderBottom: '2px solid #e8edff' }}>Data</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px', color: '#445b8f', borderBottom: '2px solid #e8edff' }}>Serviciu</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px', color: '#445b8f', borderBottom: '2px solid #e8edff' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {neachitate.map(d => (
                  <tr key={d.id} style={{ cursor: 'pointer', background: selected.has(d.id) ? '#f8faff' : '#fff' }} onClick={() => toggle(d.id)}>
                    <td style={{ padding: '9px 10px', textAlign: 'center', borderBottom: '1px solid #e8edff' }}>
                      <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} onClick={e => e.stopPropagation()} />
                    </td>
                    <td style={{ padding: '9px 10px', borderBottom: '1px solid #e8edff' }}>
                      <span className={`fin-doc-badge fin-doc-badge--tip`}>{d.tip === 'proforma' ? 'Proforma' : 'Fiscala'}</span>
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: 13, fontWeight: 600, borderBottom: '1px solid #e8edff' }}>{d.serie}-{d.numar}</td>
                    <td style={{ padding: '9px 10px', fontSize: 13, color: 'var(--muted)', borderBottom: '1px solid #e8edff' }}>{fmtDate(d.data_emitere)}</td>
                    <td style={{ padding: '9px 10px', borderBottom: '1px solid #e8edff' }}>
                      <span className={`fin-doc-badge fin-doc-badge--svc fin-doc-badge--${d.tip_serviciu}`}>{SVC_LABEL[d.tip_serviciu] || d.tip_serviciu}</span>
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: 13, fontWeight: 700, textAlign: 'right', borderBottom: '1px solid #e8edff', whiteSpace: 'nowrap' }}>{parseFloat(d.total || 0).toFixed(2)} {d.moneda}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="fm-footer" style={{ justifyContent: 'space-between' }}>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            {selected.size} din {neachitate.length} selectate
            {client.email && <> · Destinatar: <strong>{client.email}</strong></>}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={sending}>Renunta</button>
            <button className="btn btn-primary" onClick={handleSend} disabled={sending || selected.size === 0 || !(client.email || '').trim()}>
              {sending ? 'Se trimite...' : 'Trimite email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── IstoricTab ──────────────────────────────────────────── */

const TIP_LABEL = {
  client_creat:            'Client creat',
  procedura_creata:        'Procedură creată',
  procedura_actualizata:   'Procedură actualizată',
  factura_creata:          'Factură creată',
  factura_status:          'Status factură schimbat',
  email_facturi:           'Email facturi trimis',
  factura_int_creata:      'Factură internațională creată',
  factura_int_actualizata: 'Factură internațională actualizată',
};

const TIP_COLOR = {
  client_creat:            '#6366f1',
  procedura_creata:        '#10b981',
  procedura_actualizata:   '#f59e0b',
  factura_creata:          '#3b82f6',
  factura_status:          '#8b5cf6',
  email_facturi:           '#06b6d4',
  factura_int_creata:      '#ec4899',
  factura_int_actualizata: '#f97316',
};

function fmtTs(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
}

function IstoricDetail({ tip, data }) {
  if (!data || !Object.keys(data).length) return null;
  const rows = [];
  const add = (label, val) => val != null && val !== '' && rows.push({ label, val: String(val) });

  if (tip === 'procedura_creata' || tip === 'procedura_actualizata') {
    add('Tip procedură', data.tip_proc);
    add('Număr', data.numar);
    add('Denumire', data.denumire);
  } else if (tip === 'factura_creata') {
    add('Tip', data.tip);
    add('Serviciu', data.tip_serviciu);
    if (data.serie && data.numar) add('Document', `${data.serie}-${data.numar}`);
    add('Total', data.total && data.moneda ? `${data.total} ${data.moneda}` : data.total);
  } else if (tip === 'factura_status') {
    if (data.serie && data.numar) add('Document', `${data.serie}-${data.numar}`);
    add('Status anterior', data.status_vechi);
    add('Status nou', data.status_nou);
  } else if (tip === 'email_facturi') {
    add('Trimis către', data.trimis_catre);
    if (Array.isArray(data.documente)) add('Facturi', data.documente.join(', '));
    if (Array.isArray(data.failed_pdfs) && data.failed_pdfs.length)
      add('PDF-uri eșuate', data.failed_pdfs.join(', '));
  } else if (tip === 'factura_int_creata' || tip === 'factura_int_actualizata') {
    add('Oficiu', data.oficiu);
    add('Obiect', data.obiect);
    if (data.cost && data.moneda) add('Cost', `${data.cost} ${data.moneda}`);
    add('Achitat', data.achitat ? 'Da' : 'Nu');
  } else {
    Object.entries(data).forEach(([k, v]) => add(k, typeof v === 'object' ? JSON.stringify(v) : v));
  }

  if (!rows.length) return null;
  return (
    <div style={{ marginTop: 8, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
      {rows.map(({ label, val }) => (
        <div key={label} style={{ marginBottom: 3 }}>
          <span style={{ fontWeight: 600, color: '#374151' }}>{label}:</span>{' '}
          <span style={{ color: '#6b7280' }}>{val}</span>
        </div>
      ))}
    </div>
  );
}

function IstoricTab({ clientId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    getIstoricClient(clientId)
      .then(d => setEvents(d.events || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div style={{ padding: 24, color: '#9ca3af', fontSize: 14 }}>Se încarcă...</div>;
  if (!events.length) return (
    <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
      Nu există evenimente înregistrate pentru acest client.
    </div>
  );

  return (
    <div style={{ padding: '20px 0 8px' }}>
      {events.map((e, idx) => {
        const color = TIP_COLOR[e.tip] || '#9ca3af';
        const hasDetail = e.data && Object.keys(e.data).length > 0;
        return (
          <div key={e.id} style={{ display: 'flex', gap: 14 }}>
            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ width: 11, height: 11, borderRadius: '50%', background: color, marginTop: 5, flexShrink: 0 }} />
              {idx < events.length - 1 && (
                <div style={{ width: 2, flex: 1, background: '#e5e7eb', marginTop: 4 }} />
              )}
            </div>
            <div style={{ flex: 1, paddingBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                <span style={{
                  background: color, color: '#fff', borderRadius: 999,
                  padding: '2px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                }}>
                  {TIP_LABEL[e.tip] || e.tip}
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#374151' }}>
                <span style={{ fontWeight: 600 }}>{e.user || 'Sistem'}</span>
                <span style={{ color: '#9ca3af', marginLeft: 6 }}>{fmtTs(e.timestamp)}</span>
              </div>
              {hasDetail && (
                <button
                  type="button"
                  onClick={() => toggle(e.id)}
                  style={{ fontSize: 12, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 0', marginTop: 2 }}
                >
                  {expanded[e.id] ? '▲ Ascunde detalii' : '▼ Arată detalii'}
                </button>
              )}
              {expanded[e.id] && <IstoricDetail tip={e.tip} data={e.data} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const FRECVENTA_LABELS = { saptamanal: 'Saptamanal', lunar: 'Lunar', anual: 'Anual' };

function RecurringModal({ documente, rules, loading, onClose, onCancel, onCreate, showToast }) {
  const [selectedDoc, setSelectedDoc] = useState('');
  const [frecventa, setFrecventa]     = useState('lunar');
  const [repetitii, setRepetitii]     = useState('');
  const [saving, setSaving]           = useState(false);

  const activeRules = rules.filter(r => r.activ);

  const handleCreate = async () => {
    if (!selectedDoc) return showToast('Selecteaza un document');
    const n = parseInt(repetitii, 10);
    if (!n || n < 1) return showToast('Introdu numarul de repetitii');
    setSaving(true);
    const res = await onCreate(parseInt(selectedDoc, 10), frecventa, n);
    setSaving(false);
    if (res.ok) {
      showToast('Regula creata cu succes');
      setSelectedDoc(''); setRepetitii('');
    } else {
      showToast(res.data?.detail || 'Eroare la creare regula');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box fm-modal">
        <div className="modal-sticky-header fm-header">
          <div className="fm-title-row">
            <span className="fm-title">Factura recurenta</span>
            <button className="icon-circle-btn" onClick={onClose} title="Inchide">✕</button>
          </div>
        </div>

        <div className="modal-scroll-body">

          {/* Create new rule */}
          <div className="section-label">Configurare noua regula</div>
          <div className="f-row" style={{ marginBottom: 12 }}>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Document sursa</label>
              <FilterPill value={selectedDoc} onChange={e => setSelectedDoc(e.target.value)}>
                <option value="">Selecteaza document…</option>
                {documente.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.tip === 'proforma' ? 'Proforma' : 'Fiscala'} {d.serie}-{d.numar} — {parseFloat(d.total).toFixed(2)} {d.moneda}
                  </option>
                ))}
              </FilterPill>
            </div>
          </div>
          <div className="f-row" style={{ marginBottom: 16 }}>
            <div className="field">
              <label>De cate ori</label>
              <input
                type="number" min="1" className="form-control"
                placeholder="ex: 12"
                value={repetitii}
                onChange={e => setRepetitii(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Frecventa</label>
              <FilterPill value={frecventa} onChange={e => setFrecventa(e.target.value)}>
                <option value="saptamanal">Saptamanal</option>
                <option value="lunar">Lunar</option>
                <option value="anual">Anual</option>
              </FilterPill>
            </div>
          </div>
          <div style={{ textAlign: 'right', marginBottom: 28 }}>
            <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={saving}>
              {saving ? 'Se salveaza…' : 'Adauga regula'}
            </button>
          </div>

          {/* Active automations */}
          <div className="section-label">Automatizari active</div>
          {loading ? (
            <div className="doc-empty">Se incarca…</div>
          ) : activeRules.length === 0 ? (
            <div className="doc-empty">Nicio automatizare activa.</div>
          ) : (
            <table className="inv-summary-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Frecventa</th>
                  <th>Progres</th>
                  <th>Urmatoarea</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {activeRules.map(r => (
                  <tr key={r.id}>
                    <td>{r.source_serie}-{r.source_numar}</td>
                    <td>{FRECVENTA_LABELS[r.frecventa] || r.frecventa}</td>
                    <td>{r.repetitii_efectuate}/{r.numar_repetitii}</td>
                    <td>{fmtDateRO(r.data_urmatoare)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => onCancel(r.id)}>
                        Anuleaza
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function FinanciarTab({ client, proceduri, showToast }) {
  const [sectiune, setSectiune]       = useState('proforme');
  const [proceduraId, setProceduraId] = useState('');
  const [documente, setDocumente]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showFactura, setShowFactura] = useState(false);
  const [convertDoc, setConvertDoc]   = useState(null);
  const [reEditDoc,  setReEditDoc]    = useState(null);
  const [editDoc, setEditDoc]         = useState(null);
  const [duplicateDoc, setDuplicateDoc] = useState(null);
  const [showTrimite, setShowTrimite] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [recurringRules, setRecurringRules] = useState([]);
  const [recurringLoading, setRecurringLoading] = useState(false);

  const procLabel = (p) => {
    const tip = TIP_PROC_LABEL[p.tip_proc] || p.tip_proc;
    const nr = p.data?.numar || p.data?.numarDepozit || p.data?.denumire || '';
    return nr ? `${tip} · ${nr}` : tip;
  };

  const loadDocuments = useCallback(() => {
    setLoading(true);
    smartbillSvc.getDocuments(client.id)
      .then(r => setDocumente(r.data.documente || []))
      .catch(() => showToast('Eroare la incarcare documente'))
      .finally(() => setLoading(false));
  }, [client.id, showToast]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const handleStatusChange = async (docId, newStatus) => {
    try {
      const res = await smartbillSvc.patchDocument(docId, { status_plata: newStatus });
      setDocumente(ds => ds.map(d => d.id === docId ? res.data.document : d));
    } catch {
      showToast('Eroare la actualizare status');
    }
  };

  const handleDocSaved = (doc) => {
    setDocumente(ds => [doc, ...ds]);
  };

  const handleConvertSaved = (fiscala) => {
    setDocumente(ds => [fiscala, ...ds]);
  };

  const tip = sectiune === 'proforme' ? 'proforma' : 'fiscala';
  const filtered = documente.filter(d =>
    d.tip === tip && (!proceduraId || String(d.procedura_id) === String(proceduraId))
  );

  const loadRecurring = () => {
    setRecurringLoading(true);
    fetch(`/api/clienti/${client.id}/recurring/`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setRecurringRules(Array.isArray(data) ? data : []))
      .catch(() => showToast('Eroare la incarcare reguli recurente'))
      .finally(() => setRecurringLoading(false));
  };

  const openRecurring = () => { loadRecurring(); setShowRecurring(true); };

  const cancelRule = (ruleId) => {
    fetch(`/api/recurring/${ruleId}/`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ activ: false }),
    })
      .then(r => r.json())
      .then(updated => setRecurringRules(rs => rs.map(r => r.id === ruleId ? updated : r)))
      .catch(() => showToast('Eroare la anulare regula'));
  };

  const createRecurring = (docId, frecventa, numarRepetitii) => {
    return fetch(`/api/clienti/${client.id}/recurring/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_document_id: docId, frecventa, numar_repetitii: numarRepetitii }),
    }).then(r => r.json().then(d => ({ ok: r.ok, data: d })));
  };

  return (
    <div className="financiar-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowTrimite(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          Trimite facturi
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={openRecurring} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
          Factura recurenta
        </button>
      </div>
      <div className="type-tabs" style={{ marginBottom: 20 }}>
        <div className={`type-tab${sectiune === 'proforme' ? ' tt-active' : ''}`} onClick={() => setSectiune('proforme')}>Proforme</div>
        <div className={`type-tab${sectiune === 'fiscale' ? ' tt-active' : ''}`} onClick={() => setSectiune('fiscale')}>Fiscale</div>
        <div className={`type-tab${sectiune === 'international' ? ' tt-active' : ''}`} onClick={() => setSectiune('international')}>International</div>
      </div>

      {sectiune === 'international' ? (
        <InternationalTab client={client} proceduri={proceduri} showToast={showToast} />
      ) : (<>
        <div className="fin-toolbar">
          <FilterPill value={proceduraId} onChange={e => setProceduraId(e.target.value)}>
            <option value="">Toate procedurile</option>
            {(proceduri || []).map(p => (
              <option key={p.id} value={String(p.id)}>{procLabel(p)}</option>
            ))}
          </FilterPill>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowFactura(true)}>
            + Factura noua
          </button>
        </div>

        {loading ? (
          <div className="fin-empty-state"><p>Se incarca...</p></div>
        ) : filtered.length === 0 ? (
          <div className="fin-empty-state">
            <span className="fin-empty-icon">{sectiune === 'proforme' ? '📄' : '🧾'}</span>
            <p>Nicio {sectiune === 'proforme' ? 'proforma' : 'factura fiscala'} gasita.</p>
          </div>
        ) : (() => {
          const groups = [];
          const seen = new Map();
          filtered.forEach(d => {
            if (!seen.has(d.tip_serviciu)) { seen.set(d.tip_serviciu, []); groups.push(d.tip_serviciu); }
            seen.get(d.tip_serviciu).push(d);
          });
          return groups.map(svc => (
            <div key={svc} className="fin-svc-group">
              <div className={`fin-doc-badge fin-doc-badge--svc fin-doc-badge--${svc}`}>{SVC_LABEL[svc] || svc}</div>
              <div className="fin-doc-list">
                {seen.get(svc).map(d => (
                  <DocRow key={d.id} doc={d} onConvert={setConvertDoc} onStatusChange={handleStatusChange} onEdit={setEditDoc} showToast={showToast} />
                ))}
              </div>
            </div>
          ));
        })()}</>)}

      {showFactura && (
        <FacturaModal
          client={client}
          proceduri={proceduri}
          tip={tip}
          onClose={() => setShowFactura(false)}
          onSaved={handleDocSaved}
          showToast={showToast}
        />
      )}

      {convertDoc && (
        <FacturaModal
          client={client}
          proceduri={proceduri}
          convertDoc={convertDoc}
          onClose={() => setConvertDoc(null)}
          onSaved={handleConvertSaved}
          showToast={showToast}
        />
      )}

      {editDoc && (
        <InvoiceEditModal
          doc={editDoc}
          onClose={() => setEditDoc(null)}
          onSaved={updated => setDocumente(ds => ds.map(d => d.id === updated.id ? updated : d))}
          onConverted={fiscala => { setDocumente(ds => [fiscala, ...ds]); setEditDoc(null); }}
          onConvert={doc => { setConvertDoc(doc); setEditDoc(null); }}
          onReEdit={doc => { setReEditDoc(doc); setEditDoc(null); }}
          onDuplicate={doc => { setDuplicateDoc(doc); setEditDoc(null); }}
          showToast={showToast}
        />
      )}

      {duplicateDoc && (
        <FacturaModal
          client={client}
          proceduri={proceduri}
          editDoc={duplicateDoc}
          onClose={() => setDuplicateDoc(null)}
          onSaved={doc => { setDocumente(ds => [doc, ...ds]); setDuplicateDoc(null); }}
          showToast={showToast}
        />
      )}

      {reEditDoc && (
        <FacturaModal
          client={client}
          proceduri={proceduri}
          editDoc={reEditDoc}
          onClose={() => setReEditDoc(null)}
          onSaved={doc => { setDocumente(ds => [doc, ...ds]); setReEditDoc(null); }}
          showToast={showToast}
        />
      )}

      {showTrimite && (
        <TrimiteFacturiModal
          client={client}
          documente={documente}
          onClose={() => setShowTrimite(false)}
          showToast={showToast}
        />
      )}

      {showRecurring && (
        <RecurringModal
          client={client}
          documente={documente}
          rules={recurringRules}
          loading={recurringLoading}
          onClose={() => setShowRecurring(false)}
          onCancel={cancelRule}
          onCreate={createRecurring}
          showToast={showToast}
        />
      )}
    </div>
  );
}

/* ── ClientModal ─────────────────────────────────────────── */
function ClientModal({ client, onClose, onClientSaved, onClientDeleted, showToast, onBrandAdded, onBrandUpdated, onBrandDeleted, onProcChange, initialTab }) {
  const [tab, setTab] = useState(initialTab || 'proceduri');
  const [downloading, setDownloading] = useState(false);

  const handleBrief = async () => {
    setDownloading(true);
    try {
      const res = await apiClient.get(`/clienti/${client.id}/brief/`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.style.display = 'none';
      const safeName = client.nume.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
      a.download = `sumar_${safeName}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Sumar download error:', err);
      showToast('Eroare la generarea sumarului');
    } finally {
      setDownloading(false);
    }
  };
  const [proceduri, setProceduri] = useState([]);
  const [loadingProc, setLoadingProc] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [showAddBrand, setShowAddBrand] = useState(false);
  const [searchProc, setSearchProc] = useState('');
  const [searchMarca, setSearchMarca] = useState('');
  const [procOpen, setProcOpen] = useState(true);
  const [marciOpen, setMarciOpen] = useState(true);

  useEffect(() => {
    listProceduri(client.id)
      .then(d => setProceduri(d.proceduri || []))
      .catch(() => showToast('Eroare la incarcare proceduri'))
      .finally(() => setLoadingProc(false));
  }, [client.id, showToast]);

  // brandId -> sev, for brandAlertDot() below — reads the portfolio-wide alert
  // list (cheap, ~50 records) and keeps only this client's brand-renewal
  // alerts. See Phase 0, 0.5 in ai-agent-implementation-plan.md.
  const [brandSevById, setBrandSevById] = useState({});
  useEffect(() => {
    getAlerts()
      .then(alerts => {
        const map = {};
        for (const a of alerts) {
          if (a.cat === 'reinnoire' && a.brandId != null) map[a.brandId] = a.sev;
        }
        setBrandSevById(map);
      })
      .catch(() => {});
  }, [client.id]);

  const handleProcSaved = (proc, brand) => {
    setProceduri(prev => [proc, ...prev]);
    setWizardOpen(false);
    showToast('✓ Procedura salvata!');
    if (onProcChange) onProcChange(client.id, 1);
    if (brand) {
      const existing = (client.brands || []).find(b => b.id === brand.id);
      if (existing) onBrandUpdated && onBrandUpdated(brand);
      else onBrandAdded && onBrandAdded(brand);
    }
  };

  const handleProcDeleted = (id) => {
    setProceduri(prev => prev.filter(p => p.id !== id));
    if (onProcChange) onProcChange(client.id, -1);
  };

  const handleProcUpdated = (updated, brand) => {
    setProceduri(prev => prev.map(p => p.id === updated.id ? updated : p));
    if (brand) {
      const existing = (client.brands || []).find(b => b.id === brand.id);
      if (existing) onBrandUpdated && onBrandUpdated(brand);
      else onBrandAdded && onBrandAdded(brand);
    }
  };

  const handleBrandSaved = (brand, procedura) => {
    setShowAddBrand(false);
    onBrandAdded(brand);
    if (procedura) {
      setProceduri(prev => [procedura, ...prev]);
      if (onProcChange) onProcChange(client.id, 1);
    }
  };

  const openWizard = () => { setWizardOpen(w => !w); setShowAddBrand(false); };
  const openAddBrand = () => { setShowAddBrand(b => !b); setWizardOpen(false); };

  return (
    <div className="modal-overlay">
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-sticky-header">
          <div className="cj-header" style={{ marginBottom: 0 }}>
            <div className="cj-left">
              <div>
                <div className="cj-name">{client.nume}</div>
                <div className="cj-contact-row">
                  <a
                    className={`icon-circle-btn${!client.email ? ' disabled' : ''}`}
                    href={client.email ? `mailto:${client.email}` : undefined}
                    title={client.email ? `Trimite email la ${client.email}` : 'Niciun email'}
                    onClick={e => { if (!client.email) e.preventDefault(); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22 6 12 13 2 6" />
                    </svg>
                  </a>
                  <a
                    className={`icon-circle-btn${!client.telefon ? ' disabled' : ''}`}
                    href={client.telefon ? `tel:${client.telefon}` : undefined}
                    title={client.telefon ? `Suna la ${client.telefon}` : 'Niciun numar de telefon'}
                    onClick={e => { if (!client.telefon) e.preventDefault(); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </a>
                  <button
                    className={`icon-circle-btn${downloading ? ' icon-circle-btn-loading' : ''}`}
                    onClick={handleBrief}
                    disabled={downloading}
                    title={downloading ? 'Se genereaza sumarul...' : 'Descarca sumar client — ce e activ acum (Word)'}
                  >
                    {downloading ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-spin">
                        <path d="M21 12a9 9 0 1 1-9-9"/>
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="cj-right">
              <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>✕</button>
            </div>
          </div>
          <div className="modal-tabs">
            <button type="button" className={`modal-tab${tab === 'proceduri' ? ' mt-active' : ''}`} onClick={() => setTab('proceduri')}>Actiuni</button>
            <button type="button" className={`modal-tab${tab === 'detalii' ? ' mt-active' : ''}`} onClick={() => setTab('detalii')}>Detalii client</button>
            <button type="button" className={`modal-tab${tab === 'financiar' ? ' mt-active' : ''}`} onClick={() => setTab('financiar')}>Financiar</button>
            <button type="button" className={`modal-tab${tab === 'istoric' ? ' mt-active' : ''}`} onClick={() => setTab('istoric')}>Istoric client</button>
          </div>
        </div>

        <div className="modal-scroll-body">
          {tab === 'detalii' && (
            <ClientDetailsForm editClient={client} onSaved={onClientSaved} onDeleted={onClientDeleted} showToast={showToast} proceduri={proceduri} />
          )}
          {tab === 'financiar' && (
            <FinanciarTab client={client} proceduri={proceduri} showToast={showToast} />
          )}
          {tab === 'istoric' && (
            <IstoricTab clientId={client.id} />
          )}
          {tab === 'proceduri' && (
            <div className="brands-section">
              <div className="tab-actions-row">
                <button className="btn-action-square" type="button" onClick={openWizard}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  <span>Adauga Procedura</span>
                </button>
                <button className="btn-action-square" type="button" onClick={openAddBrand}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  <span>Adauga Marca</span>
                </button>
              </div>

              {wizardOpen && (
                <Wizard
                  clientId={client.id}
                  onClose={() => setWizardOpen(false)}
                  onSaved={handleProcSaved}
                  showToast={showToast}
                />
              )}

              {showAddBrand && (
                <AddBrandForm
                  clientId={client.id}
                  onCancel={() => setShowAddBrand(false)}
                  onSaved={handleBrandSaved}
                  showToast={showToast}
                />
              )}

              <div className="sec-head" style={{ marginTop: 18, marginBottom: 6, cursor: 'pointer', userSelect: 'none' }} onClick={() => setProcOpen(o => !o)}>
                <div className="sec-title">Proceduri <span className="sec-count">{proceduri.length}</span></div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform .2s', transform: procOpen ? 'rotate(0deg)' : 'rotate(-90deg)', flexShrink: 0 }}><polyline points="6 9 12 15 18 9"/></svg>
              </div>

              {procOpen && <>
                {proceduri.length > 0 && (
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Cauta procedura..."
                    value={searchProc}
                    onChange={e => setSearchProc(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                )}

                {loadingProc && <div className="empty-text" style={{ padding: 10 }}>Se incarca...</div>}

                {!loadingProc && proceduri.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                    </div>
                    <div className="empty-text">Nicio procedura adaugata inca.</div>
                  </div>
                )}

                {(() => {
                  const q = searchProc.trim().toLowerCase();
                  const filtered = q
                    ? proceduri.filter(p => {
                        const d = p.data || {};
                        return [TIP_PROC_LABEL[p.tip_proc] || p.tip_proc, d.denumire, d.numar, d.dataDepunere]
                          .some(v => v && String(v).toLowerCase().includes(q));
                      })
                    : proceduri;
                  return filtered.map(p => (
                    <ProceduraItem key={p.id} proc={p} onDelete={handleProcDeleted} onUpdated={handleProcUpdated} showToast={showToast} />
                  ));
                })()}
              </>}

              <div className="sec-head" style={{ marginTop: 18, marginBottom: 6, cursor: 'pointer', userSelect: 'none' }} onClick={() => setMarciOpen(o => !o)}>
                <div className="sec-title">Marci <span className="sec-count">{client.brands.length}</span></div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform .2s', transform: marciOpen ? 'rotate(0deg)' : 'rotate(-90deg)', flexShrink: 0 }}><polyline points="6 9 12 15 18 9"/></svg>
              </div>

              {marciOpen && <>
                {client.brands.length > 0 && (
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Cauta marca..."
                    value={searchMarca}
                    onChange={e => setSearchMarca(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                )}

                {client.brands.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41 13 21l-9-9V4h8l9 9a2 2 0 0 1 0 2.82Z"/><circle cx="7.5" cy="7.5" r="1.5"/></svg>
                    </div>
                    <div className="empty-text">Nicio marca adaugata inca.</div>
                  </div>
                )}

                {(() => {
                  const q = searchMarca.trim().toLowerCase();
                  const filtered = q
                    ? client.brands.filter(b => b.name && b.name.toLowerCase().includes(q))
                    : client.brands;
                  return filtered.map(b => (
                    <BrandItem key={b.id} brand={b} alertSev={brandSevById[b.id]} showToast={showToast} onBrandDeleted={onBrandDeleted}
                      onBrandUpdated={(updatedBrand, procedura) => {
                        if (onBrandUpdated) onBrandUpdated(updatedBrand);
                        if (procedura) handleProcUpdated(procedura, null);
                      }}
                    />
                  ));
                })()}
              </>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── ClientiPage ─────────────────────────────────────────── */
export default function ClientiPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, viewMode } = useAuth();
  const [search, setSearch] = useState('');
  const [societateFilter, setSocietateFilter] = useState(viewMode);

  // Keep local filter in sync when the global view mode changes from the sidebar
  useEffect(() => { setSocietateFilter(viewMode); }, [viewMode]);
  const [modalClientId, setModalClientId] = useState(null);
  const [modalInitialTab, setModalInitialTab] = useState(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const { toasts, show: showToast } = useToast();
  const location = useLocation();

  useEffect(() => {
    listClienti()
      .then(data => setClients(data.clients || []))
      .catch(() => showToast('Nu am putut incarca clientii'))
      .finally(() => setLoading(false));
  }, [showToast]);

  useEffect(() => {
    // location.state covers in-app navigation (e.g. clicking an alert); the
    // query param covers external links (e.g. the daily report email).
    const id = location.state?.openClientId || new URLSearchParams(location.search).get('openClientId');
    if (!id || clients.length === 0) return;
    setModalClientId(Number(id));
    setModalInitialTab(location.state?.openTab || null);
    setShowAddClient(false);
    window.history.replaceState({}, '', location.pathname);
  }, [clients, location.state?.openClientId, location.search]);

  const filtered = clients.filter(c => {
    const matchSearch = !search ||
      c.nume.toLowerCase().includes(search.toLowerCase()) ||
      c.cui?.toLowerCase().includes(search.toLowerCase()) ||
      c.cnp?.includes(search);
    const matchSocietate = !societateFilter || (c.alocat_la || '') === societateFilter;
    return matchSearch && matchSocietate;
  });

  const modalClient = clients.find(c => c.id === modalClientId);

  const openClient = (id) => {
    setModalClientId(id);
    setShowAddClient(false);
  };

  const handleClientCreated = (client) => {
    setClients(prev => [...prev, { ...client, brands: [] }]);
    setShowAddClient(false);
    setModalClientId(client.id);
  };

  const handleClientSaved = (client) => {
    setClients(prev => prev.map(c => c.id === client.id ? { ...c, ...client } : c));
  };

  const handleClientDeleted = (id) => {
    setClients(prev => prev.filter(c => c.id !== id));
    setModalClientId(null);
  };

  const handleBrandAdded = (brand) => {
    setClients(prev => prev.map(c => c.id === modalClientId ? { ...c, brands: [...c.brands, brand] } : c));
  };

  const handleBrandUpdated = (brand) => {
    setClients(prev => prev.map(c =>
      c.id === modalClientId ? { ...c, brands: c.brands.map(b => b.id === brand.id ? brand : b) } : c
    ));
  };

  const handleBrandDeleted = (brandId) => {
    setClients(prev => prev.map(c =>
      c.id === modalClientId ? { ...c, brands: c.brands.filter(b => b.id !== brandId), nr_branduri: (c.nr_branduri || 1) - 1 } : c
    ));
  };

  const handleProcChange = (clientId, delta) => {
    setClients(prev => prev.map(c =>
      c.id === clientId ? { ...c, nr_proceduri: (c.nr_proceduri || 0) + delta } : c
    ));
  };

  return (
    <main className="cp-main">
      <div className="topbar">
        <div className="topbar-left">
          <h1 className="page-title">Clienti</h1>
          <span className="count-badge">{filtered.length !== clients.length ? `${filtered.length} / ${clients.length}` : clients.length}</span>
        </div>
        <div className="topbar-right">
          <button className="btn btn-primary btn-sm" type="button" onClick={() => { setShowAddClient(b => !b); setModalClientId(null); }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Adauga Client
          </button>
        </div>
      </div>

      <div className="content">
        <div className="search-row">
          <div className="search-wrap">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input className="search-input" type="text" placeholder="Cauta dupa nume, CUI, CNP..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {(user?.is_superuser || (user?.societate || '') !== 'inventa') && (
            <FilterPill value={societateFilter} onChange={e => setSocietateFilter(e.target.value)}>
              <option value="">Toate societatile</option>
              <option value="alienzaz">Alienzaz / Cabinet</option>
              <option value="inventa">Inventa</option>
            </FilterPill>
          )}
        </div>

        {showAddClient && (
          <AddClientPanel
            onClose={() => setShowAddClient(false)}
            onSaved={handleClientCreated}
            showToast={showToast}
          />
        )}

        <div className="clients-card">
          <table className="clients-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Monitorizare</th>
                <th>Proceduri</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: 24, color: 'var(--muted)' }}>Se incarca...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: 24, color: 'var(--muted)' }}>Niciun client gasit.</td></tr>
              )}
              {filtered.map(c => (
                <tr key={c.id} className={modalClientId === c.id ? 'row-selected' : ''} onClick={() => openClient(c.id)}>
                  <td className="client-name-cell">
                    <div className="cn-name">{c.nume}</div>
                    <div className="cn-sub">{c.tip === 'PJ' ? `${c.forma_juridica} · ${c.cui}` : `CNP: ${c.cnp}`}</div>
                  </td>
                  <td className="num-cell">{c.nr_branduri} <span>monitorizari</span></td>
                  <td className="num-cell">{c.nr_proceduri} <span>proceduri</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalClient && (
        <ClientModal
          key={modalClient.id}
          client={modalClient}
          onClose={() => { setModalClientId(null); setModalInitialTab(null); }}
          onClientSaved={handleClientSaved}
          onClientDeleted={handleClientDeleted}
          showToast={showToast}
          onBrandAdded={handleBrandAdded}
          onBrandUpdated={handleBrandUpdated}
          onBrandDeleted={handleBrandDeleted}
          onProcChange={handleProcChange}
          initialTab={modalInitialTab}
        />
      )}

      <ToastWrap toasts={toasts} />
    </main>
  );
}
